import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { md5 as photoCompatibilityHash } from 'js-md5';
import { Observable, catchError, forkJoin, finalize, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { AppInfo, Building, ImageListItem, ImprovementsPayload, Job, JobPayload, OtherBuilding, PropertyInfo, PropertyPhoto, ReleaseNotes, ReportTypeOption, RuralInfo, ValuationTypeOption, emptyBuilding } from '../model/tops.models';
import { AuditAction } from '../model/audit.models';
import { AuditService } from './audit.service';
import { ConnectivityService } from './connectivity.service';
import { IndexedDbStorageService } from './indexed-db-storage.service';
import { SecurityEventService } from './security-event.service';

export interface RemoteJobDetails {
  raw: Record<string, unknown>;
  job: JobPayload;
  propertyInfo: Partial<PropertyInfo>;
  photos: PropertyPhoto[];
  valuationTypes: ValuationTypeOption[];
  imageList: ImageListItem[];
}

export interface AssignedRemoteJobSummary {
  trackerId: number;
  jobNumber: string | null;
  address: string;
  appointmentDate: string | null;
  appointmentTime: string | null;
  reportType: string | null;
  client: string | null;
}

export interface SyncOfflineDataResponse {
  ok: boolean;
  messageId: string | null;
  payloadBytes: number;
}

export interface SyncStatusResponse {
  pendingJobIds: string[];
  pendingJobCount: number;
  lastSyncedAt: string | null;
}

export interface ImageUploadTarget {
  clientUploadId?: string;
  id: number | string | null;
  jobId: number | string;
  mcProId?: string | number | null;
  label: string | null;
  description?: string | null;
  documentUid: string;
  md5Hash: string;
  s3Bucket: string;
  s3Key: string;
  contentType: string;
  uploadUrl: string;
}

export interface ImagePresignResponse {
  jobId: number | string;
  photos: ImageUploadTarget[];
}

export interface ImagePresignPhotoRequest {
  clientUploadId: string;
  id: number | string | null;
  jobId: number | string;
  mcProId?: string | number | null;
  label: string | null;
  description?: string | null;
  contentType: string;
  md5Hash: string;
}

type PhotoDetailsPayload = Pick<PropertyPhoto, 'label' | 'description' | 'imageType' | 'displayOrder'>;

export interface JobSyncRecord {
  job: Job | JobPayload;
  propertyInfo: Partial<PropertyInfo> | null;
  photos: PropertyPhoto[];
}

export interface JobSyncConflict {
  path: string;
  section: string;
  label: string;
  fieldLabel: string;
  serverValue: unknown;
  localValue: unknown;
  valueKind?: 'text' | 'image';
  selected: 'server' | 'local';
  customValue?: string;
}

export type JobSyncContentMode = 'fields-and-photos' | 'fields-only' | 'photos-only';

export interface JobSyncDraft {
  jobId: number | string;
  jobNumber: string | null;
  server: JobSyncRecord;
  local: JobSyncRecord;
  conflicts: JobSyncConflict[];
}

const REMOTE_API_BASE = `${environment.backendApiBaseUrl}/jobs`;
const API_BASE = environment.backendApiBaseUrl;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly storage = inject(IndexedDbStorageService);
  private readonly securityEvents = inject(SecurityEventService);
  private readonly audit = inject(AuditService);
  private readonly localJobsKey = 'tops-offline.localJobs';
  private readonly localPropertyInfoPrefix = 'tops-offline.propertyInfo.';
  private readonly localPhotosPrefix = 'tops-offline.photos.';
  private readonly localImageListKey = 'tops-offline.imageList';
  private localPhotoIdSequence = 0;
  private memoryJobs: Job[] = [];
  private readonly lastNotesByJob = new Map<string, string>();

  getJobs(): Observable<Job[]> {
    if (!this.useBackend()) return this.withStorageReady(() => this.readLocalJobs());

    return this.http.get<Job[]>(`${API_BASE}/jobs`).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.readLocalJobs())),
    );
  }

  getJob(id: number | string): Observable<Job> {
    if (!this.useBackend()) return this.withStorageReady(() => this.localJobValue(id));

    return this.http.get<Job>(`${API_BASE}/jobs/${id}`).pipe(
      catchError((error) => this.withLocalFallback(error, () => {
        const job = this.readLocalJobs().find((item) => String(item.id) === String(id));
        if (!job) throw error;
        return job;
      })),
    );
  }

  getRemoteJobDetails(jobNumber: string): Observable<RemoteJobDetails> {
    return this.withActiveSession(() => this.getRemoteJobDetailsAfterAuth(jobNumber));
  }

  private getRemoteJobDetailsAfterAuth(jobNumber: string): Observable<RemoteJobDetails> {
    const encodedJobNumber = encodeURIComponent(jobNumber.trim());
    return this.http.get<Record<string, unknown>>(`${REMOTE_API_BASE}/${encodedJobNumber}`).pipe(
      map((remote) => this.mapRemoteJobDetails(jobNumber, remote)),
      tap((details) => {
        if (details.imageList.length) this.writeLocalImageList(details.imageList);
      }),
    );
  }

  getAssignedRemoteJobs(username: string): Observable<AssignedRemoteJobSummary[]> {
    return this.withActiveSession(() => this.http.post<Record<string, unknown>[]>(`${REMOTE_API_BASE}/assigned`, { username }).pipe(
      map((remoteJobs) => remoteJobs.map((remote) => this.mapAssignedRemoteJobSummary(remote))),
      tap((jobs) => this.audit.track({
        category: 'DATA_ACCESS',
        action: AuditAction.JOB_LIST_VIEWED,
        message: 'Assigned job list retrieved.',
        detail: { resultCount: jobs.length },
      })),
    ));
  }

  getAssignedRemoteJobDetails(username: string, trackerIds: number[]): Observable<RemoteJobDetails[]> {
    return this.withActiveSession(() => this.http.post<Record<string, unknown>[]>(`${REMOTE_API_BASE}/assigned/details`, { username, trackerIds }).pipe(
      map((remoteJobs) => remoteJobs.map((remote) => {
        const jobNumber = this.remoteString(remote, 'jobNumber', 'JobNumber') || '';
        return this.mapRemoteJobDetails(jobNumber, remote);
      })),
      tap((detailsList) => {
        const imageList = detailsList.find((details) => details.imageList.length)?.imageList;
        if (imageList?.length) this.writeLocalImageList(imageList);
        // Story 3579376: records pulled from the web app down to the device, per user.
        this.audit.track({
          category: 'DATA_ACCESS',
          action: AuditAction.JOB_DOWNLOADED_FOR_OFFLINE,
          message: 'Job records downloaded to the device for offline use.',
          detail: { resultCount: detailsList.length, imageListCount: imageList?.length ?? 0 },
        });
      }),
    ));
  }

  getImageList(): Observable<ImageListItem[]> {
    return this.withStorageReady(() => this.readLocalImageList());
  }

  private readonly mcproReportTypesCache = signal<ReportTypeOption[]>([]);
  private reportTypesLoaded = false;
  private reportTypesLoading = false;

  /** App-wide cache of report types. Kicks off a background fetch if the cache isn't loaded yet. */
  readonly mcproReportTypes = (): ReportTypeOption[] => {
    if (!this.reportTypesLoaded && !this.reportTypesLoading) {
      this.reportTypesLoading = true;
      this.loadMcproReportTypes().subscribe({ complete: () => this.reportTypesLoading = false });
    }
    return this.mcproReportTypesCache();
  };

  ensureReportTypesLoaded(): Observable<ReportTypeOption[]> {
    // Retry the fetch if a prior attempt left the cache empty (e.g. failed or returned no data).
    if (this.reportTypesLoaded && this.mcproReportTypesCache().length) return of(this.mcproReportTypesCache());
    return this.loadMcproReportTypes();
  }

  /** Forces a fresh fetch from the backend, refreshing the cache. Call after a successful login. */
  loadMcproReportTypes(): Observable<ReportTypeOption[]> {
    return this.http.get<ReportTypeOption[]>(`${API_BASE}/report-types/mcpro`).pipe(
      tap((options) => {
        this.reportTypesLoaded = true;
        this.mcproReportTypesCache.set(Array.isArray(options) ? options : []);
      }),
      // Keep whatever is already cached if the lookup fails.
      catchError(() => of(this.mcproReportTypes())),
    );
  }

  createJob(payload: JobPayload): Observable<Job> {
    if (!this.useBackend()) return this.withStorageReady(() => this.createLocalJob(payload));

    return this.http.post<Job>(`${API_BASE}/jobs`, payload).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.createLocalJob(payload))),
    );
  }

  updateJob(id: number | string, payload: Partial<JobPayload>): Observable<Job> {
    if (!this.useBackend()) return this.withStorageReady(() => this.updateLocalJob(id, payload, new Error('Job not found.')));

    return this.http.put<Job>(`${API_BASE}/jobs/${id}`, payload).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.updateLocalJob(id, payload, error))),
    );
  }

  deleteJob(id: number | string): Observable<void> {
    if (!this.useBackend()) return this.withStorageReady(() => this.deleteLocalJob(id));

    return this.http.delete<void>(`${API_BASE}/jobs/${id}`).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.deleteLocalJob(id))),
    );
  }

  archiveJob(id: number | string): Observable<Job> {
    if (!this.useBackend()) return this.withStorageReady(() => this.updateLocalJob(id, { status: 'Archived' }, new Error('Job not found.')));

    return this.http.patch<Job>(`${API_BASE}/jobs/${id}/archive`, {}).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.updateLocalJob(id, { status: 'Archived' }, error))),
    );
  }

  getPropertyInfo(jobId: number | string): Observable<PropertyInfo | null> {
    const source = !this.useBackend()
      ? this.withStorageReady(() => this.readLocalPropertyInfo(jobId))
      : this.http.get<PropertyInfo | null>(`${API_BASE}/jobs/${jobId}/property-info`).pipe(
          catchError((error) => this.withLocalFallback(error, () => this.readLocalPropertyInfo(jobId))),
        );

    // Seed the note baseline so an unchanged note is not later counted as newly created.
    return source.pipe(tap((info) => this.lastNotesByJob.set(String(jobId), this.notesText(info))));
  }

  savePropertyInfo(jobId: number | string, payload: PropertyInfo): Observable<PropertyInfo> {
    const source = !this.useBackend()
      ? this.withStorageReady(() => this.writeLocalPropertyInfo(jobId, payload))
      : this.http.post<PropertyInfo>(`${API_BASE}/jobs/${jobId}/property-info`, payload).pipe(
          catchError((error) => this.withLocalFallback(error, () => this.writeLocalPropertyInfo(jobId, payload))),
        );

    return source.pipe(tap(() => this.auditPropertyInfoSaved(jobId, payload)));
  }

  private auditPropertyInfoSaved(jobId: number | string, payload: PropertyInfo): void {
    this.audit.track({
      logSource: this.connectivity.currentOnline ? 'ONLINE' : 'OFFLINE',
      category: 'DATA_CHANGE',
      action: AuditAction.JOB_SAVED,
      entityType: 'PropertyInfo',
      message: 'Property information saved.',
      detail: { jobId, hasNotes: !!this.notesText(payload) },
    });

    // Story 3579376 counts notes created, so only a changed non-empty note is an event.
    const notes = this.notesText(payload);
    const key = String(jobId);
    if (notes && notes !== this.lastNotesByJob.get(key)) {
      this.audit.track({
        category: 'NOTE',
        action: AuditAction.NOTE_CREATED,
        entityType: 'PropertyInfo',
        message: 'Property note captured by end user.',
        detail: { jobId, noteLength: notes.length },
      });
    }
    this.lastNotesByJob.set(key, notes);
  }

  private notesText(info: PropertyInfo | null | undefined): string {
    return (info?.notes ?? '').trim();
  }

  saveMultiDwelling(trackerId: string, payload: ImprovementsPayload): Observable<Building[]> {
    return this.withActiveSession(() => {
      const encodedTrackerId = encodeURIComponent(trackerId.trim());
      return this.http.post<Building[]>(`${REMOTE_API_BASE}/${encodedTrackerId}/multi-dwelling`, payload).pipe(
        tap((buildings) => this.audit.track({
          category: 'DATA_CHANGE',
          action: AuditAction.MULTI_DWELLING_SAVED,
          entityType: 'Tracker',
          message: 'Multi-dwelling improvements saved.',
          detail: { trackerId, buildingCount: buildings.length },
        })),
      );
    });
  }

  getPhotos(jobId: number | string): Observable<PropertyPhoto[]> {
    if (!this.useBackend()) return this.withStorageReady(() => this.normalizePhotoList(this.readLocalPhotos(jobId), jobId));

    return this.http.get<unknown>(`${API_BASE}/jobs/${encodeURIComponent(String(jobId))}/photos`).pipe(
      map((photos) => this.normalizePhotoList(photos, jobId)),
      catchError((error) => this.withLocalFallback(error, () => this.normalizePhotoList(this.readLocalPhotos(jobId), jobId))),
    );
  }

  getPendingUploadPhotos(jobId: number | string): Observable<PropertyPhoto[]> {
    return this.getPhotos(jobId).pipe(
      map((photos) => photos.filter((photo) => this.isPendingLocalUploadPhoto(photo))),
    );
  }

  getDisplayPhotos(jobId: number | string): Observable<PropertyPhoto[]> {
    return this.getPhotos(jobId).pipe(
      switchMap((photos) => from(this.materializeImportedPhotos(photos)).pipe(
        switchMap((materializedPhotos) => this.saveMaterializedPhotos(jobId, photos, materializedPhotos)),
      )),
    );
  }

  uploadPhoto(jobId: number | string, formData: FormData): Observable<PropertyPhoto> {
    return this.withActiveSession(() => this.uploadPhotoAfterAuth(jobId, formData));
  }

  private uploadPhotoAfterAuth(jobId: number | string, formData: FormData): Observable<PropertyPhoto> {
    if (!this.useBackend()) {
      return from(this.storage.ready()).pipe(
        switchMap(() => this.createLocalUploadedPhoto(jobId, formData)),
      );
    }

    return this.createApiUploadedPhoto(jobId, formData);
  }

  saveImportedPhotos(jobId: number | string, photos: PropertyPhoto[]): Observable<PropertyPhoto[]> {
    return this.withActiveSession(() => this.saveImportedPhotosAfterAuth(jobId, photos));
  }

  private saveImportedPhotosAfterAuth(jobId: number | string, photos: PropertyPhoto[]): Observable<PropertyPhoto[]> {
    const normalizedPhotos = this.normalizePhotoList(photos, jobId);
    if (!normalizedPhotos.length) return of([]);
    if (!this.useBackend()) {
      return from(this.storage.ready()).pipe(
        switchMap(() => from(this.materializeImportedPhotos(normalizedPhotos))),
        map((materializedPhotos) => this.writeImportedLocalPhotos(jobId, materializedPhotos)),
      );
    }

    return from(this.materializeImportedPhotos(normalizedPhotos)).pipe(
      switchMap((materializedPhotos) => this.http.post<PropertyPhoto[]>(`${API_BASE}/jobs/${jobId}/imported-photos`, { photos: materializedPhotos }).pipe(
        map((savedPhotos) => this.normalizePhotoList(savedPhotos, jobId)),
        catchError((error) => this.withLocalFallback(error, () => this.writeImportedLocalPhotos(jobId, materializedPhotos))),
      )),
    );
  }

  private saveMaterializedPhotos(jobId: number | string, originalPhotos: PropertyPhoto[], materializedPhotos: PropertyPhoto[]): Observable<PropertyPhoto[]> {
    const changed = materializedPhotos.some((photo, index) => photo.photoUrl !== originalPhotos[index]?.photoUrl);
    if (!changed) return of(materializedPhotos);
    if (!this.useBackend()) return this.withStorageReady(() => this.writeImportedLocalPhotos(jobId, materializedPhotos));

    return this.http.post<PropertyPhoto[]>(`${API_BASE}/jobs/${jobId}/imported-photos`, { photos: materializedPhotos }).pipe(
      map((savedPhotos) => this.normalizePhotoList(savedPhotos, jobId)),
      catchError((error) => this.withLocalFallback(error, () => this.writeImportedLocalPhotos(jobId, materializedPhotos))),
    );
  }

  saveWebcamPhoto(jobId: number | string, imageData: string, label: string | null, description: string | null = null, imageType: string | null = null, displayOrder: string | null = null): Observable<PropertyPhoto> {
    return this.withActiveSession(() => this.saveWebcamPhotoAfterAuth(jobId, imageData, label, description, imageType, displayOrder));
  }

  private saveWebcamPhotoAfterAuth(jobId: number | string, imageData: string, label: string | null, description: string | null = null, imageType: string | null = null, displayOrder: string | null = null): Observable<PropertyPhoto> {
    if (!this.useBackend()) return this.withStorageReady(() => this.createLocalPhoto(jobId, imageData, label, description, imageType, displayOrder));

    const source = this.http.post<PropertyPhoto>(`${API_BASE}/jobs/${jobId}/webcam-photo`, { imageData, label, description, imageType, displayOrder, md5Hash: this.photoMd5Hash(imageData) }).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.createLocalPhoto(jobId, imageData, label, description, imageType, displayOrder))),
    );

    return source.pipe(tap(() => this.audit.track({
      logSource: this.connectivity.currentOnline ? 'ONLINE' : 'OFFLINE',
      category: 'PHOTO',
      action: AuditAction.PHOTO_CAPTURED,
      entityType: 'Job',
      message: 'Photo captured on device.',
      detail: { jobId, label, imageType, displayOrder },
    })));
  }

  updatePhoto(jobId: number | string, photoId: number | string, payload: PhotoDetailsPayload): Observable<PropertyPhoto> {
    if (!this.useBackend()) return this.withStorageReady(() => this.updateLocalPhoto(jobId, photoId, payload, new Error('Photo not found.')));

    return this.http.put<PropertyPhoto>(`${API_BASE}/jobs/${jobId}/photos/${photoId}`, payload).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.updateLocalPhoto(jobId, photoId, payload, error))),
    );
  }

  deletePhoto(photoId: number | string): Observable<void> {
    if (!this.useBackend()) return this.withStorageReady(() => this.deleteLocalPhoto(photoId));

    return this.http.delete<void>(`${API_BASE}/photos/${photoId}`).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.deleteLocalPhoto(photoId))),
    );
  }

  deleteLocalPendingPhoto(photoId: number | string): Observable<void> {
    return this.withStorageReady(() => this.deleteLocalPhoto(photoId));
  }

  openPhotosFolder(jobId: number | string): Observable<{ ok?: boolean; path?: string }> {
    if (!this.useBackend()) return of({ ok: false, path: 'Browser IndexedDB' });

    return this.http.post<{ ok?: boolean; path?: string }>(`${API_BASE}/open-photos-folder`, { jobId }).pipe(
      catchError((error) => this.withLocalFallback(error, () => ({ ok: false, path: 'Browser IndexedDB' }))),
    );
  }

  emailTops(jobId: number | string, jobNumber: string): Observable<{ photoCount: number; message?: string; code?: string }> {
    return this.withActiveSession(() => this.emailTopsAfterAuth(jobId, jobNumber));
  }

  private emailTopsAfterAuth(jobId: number | string, jobNumber: string): Observable<{ photoCount: number; message?: string; code?: string }> {
    if (!this.useBackend()) {
      return this.withStorageReady(() => ({ photoCount: this.readLocalPhotos(jobId).length, message: jobNumber, code: 'offline' }));
    }

    return this.http.post<{ photoCount: number; message?: string; code?: string }>(`${API_BASE}/jobs/${jobId}/email-tops`, { jobNumber }).pipe(
      catchError((error) => this.withLocalFallback(error, () => ({
        photoCount: this.readLocalPhotos(jobId).length,
        message: jobNumber,
        code: 'offline',
      }))),
    );
  }

  syncOfflineDataToSqs(): Observable<SyncOfflineDataResponse> {
    return this.withActiveSession(() => {
      const offlineError = this.offlineSyncError();
      if (offlineError) return offlineError;

      return from(this.storage.ready()).pipe(
        switchMap(() => this.validateOfflineIntegrity()),
        switchMap(() => this.http.post<SyncOfflineDataResponse>(`${API_BASE}/sync/sqs`, this.offlineDataSyncPayload())),
      );
    });
  }

  prepareJobSync(job: Job): Observable<JobSyncDraft> {
    return this.withActiveSession(() => {
      if (!job.jobNumber) return throwError(() => new Error('Job number is required before syncing.'));
      const jobNumber = job.jobNumber;
      const offlineError = this.offlineSyncError<JobSyncDraft>();
      if (offlineError) return offlineError;

      return from(this.storage.ready()).pipe(
        switchMap(() => this.validateOfflineIntegrity<JobSyncDraft>()),
        switchMap(() => forkJoin({
          serverDetails: this.getRemoteJobDetailsAfterAuth(jobNumber),
          local: this.localJobSyncRecord(job.id),
        })),
        map(({ serverDetails, local }) => {
          const server = this.serverJobSyncRecord(job.id, serverDetails);
          return {
            jobId: job.id,
            jobNumber: job.jobNumber,
            server,
            local,
            conflicts: this.buildJobSyncConflicts(server, local),
          };
        }),
        tap((draft) => {
          if (!draft.conflicts.length) return;
          this.audit.track({
            logSource: 'ONLINE',
            category: 'CONFLICT',
            action: AuditAction.CONFLICT_DETECTED,
            severity: 'WARNING',
            entityType: 'Job',
            jobNumber: draft.jobNumber ?? undefined,
            message: 'Sync conflicts detected and presented to the end user.',
            detail: { fields: draft.conflicts.map((conflict) => conflict.path) },
          });
        }),
      );
    });
  }

  syncResolvedJobToSqs(draft: JobSyncDraft, conflicts: JobSyncConflict[], contentMode: JobSyncContentMode = 'fields-and-photos'): Observable<SyncOfflineDataResponse> {
    return this.withActiveSession(() => {
      const offlineError = this.offlineSyncError();
      if (offlineError) return offlineError;

      this.audit.beginTransaction();
      this.auditConflictResolution(draft, conflicts);
      return this.validateOfflineIntegrity().pipe(
        switchMap(() => {
          const resolvedRecord = this.applySyncSelections(draft.local, draft.server, conflicts);
          const localPayload = this.jobSyncPayload(draft.jobId, resolvedRecord);
          if (contentMode === 'fields-only') {
            return this.postJobSyncPayload(draft, this.fieldValuesOnlyJobSyncPayload(draft.jobId, resolvedRecord), localPayload, 'fields-only', 0);
          }

          const uploadablePhotos = resolvedRecord.photos.filter((photo) => this.isPendingLocalUploadPhoto(photo));
          if (!uploadablePhotos.length) {
            const payload = contentMode === 'photos-only'
              ? this.photosOnlyJobSyncPayload(draft.jobId, resolvedRecord, draft.server, [], [])
              : this.documentJobSyncPayload(draft.jobId, resolvedRecord, [], []);
            return this.postJobSyncPayload(draft, payload, localPayload, contentMode, 0);
          }

          const presignPhotos = this.imagePresignPhotoRequests(uploadablePhotos);
          this.audit.track({
            logSource: 'ONLINE',
            category: 'PHOTO',
            action: AuditAction.PHOTO_PRESIGN_REQUESTED,
            entityType: 'Job',
            jobNumber: draft.jobNumber ?? undefined,
            message: 'Requested presigned upload URLs for offline photos.',
            detail: {
              jobId: draft.jobId,
              photoCount: uploadablePhotos.length,
              uploadRequestCount: presignPhotos.length,
            },
          });

          return this.presignJobImages(this.remoteJobIdentifier(draft, resolvedRecord), presignPhotos).pipe(
            map((response) => this.attachClientUploadIds(response.photos, presignPhotos)),
            switchMap((uploadTargets) => from(this.uploadImagesToS3(uploadablePhotos, uploadTargets)).pipe(map(() => uploadTargets))),
            tap(() => this.audit.track({
              logSource: 'ONLINE',
              category: 'PHOTO',
              action: AuditAction.PHOTO_UPLOADED,
              entityType: 'Job',
              jobNumber: draft.jobNumber ?? undefined,
              message: 'Offline photos uploaded to storage.',
              detail: {
                jobId: draft.jobId,
                photoCount: uploadablePhotos.length,
              },
            })),
            switchMap((uploadedPhotos) => this.postJobSyncPayload(
              draft,
              contentMode === 'photos-only'
                ? this.photosOnlyJobSyncPayload(draft.jobId, resolvedRecord, draft.server, uploadedPhotos, uploadablePhotos)
                : this.documentJobSyncPayload(draft.jobId, resolvedRecord, uploadedPhotos, uploadablePhotos),
              this.localJobSyncPayloadWithUploadedPhotos(draft.jobId, resolvedRecord, uploadedPhotos, uploadablePhotos),
              contentMode,
              uploadedPhotos.length,
            )),
          );
        }),
      );
    });
  }

  /** Story 3579376 counts conflicts resolved by the end user. */
  private auditConflictResolution(draft: JobSyncDraft, conflicts: JobSyncConflict[]): void {
    if (!conflicts.length) return;

    this.audit.track({
      logSource: 'ONLINE',
      category: 'CONFLICT',
      action: AuditAction.CONFLICT_RESOLVED,
      entityType: 'Job',
      jobNumber: draft.jobNumber ?? undefined,
      message: 'End user resolved sync conflicts.',
      detail: { fields: conflicts.map((conflict) => conflict.path) },
    });
  }

  private postJobSyncPayload(
    draft: JobSyncDraft,
    payload: Record<string, unknown>,
    localPayload: Record<string, unknown>,
    contentMode: JobSyncContentMode,
    photoCount: number,
  ): Observable<SyncOfflineDataResponse> {
    return this.http.post<SyncOfflineDataResponse>(`${API_BASE}/sync/jobs/${encodeURIComponent(String(draft.jobId))}/sqs`, payload).pipe(
      tap((response) => {
        this.applyLocalPhotoSyncPayload(draft.jobId, localPayload);
        this.audit.track({
          logSource: 'ONLINE',
          category: 'SYNC',
          action: this.syncAction(contentMode, photoCount),
          entityType: 'Job',
          jobNumber: draft.jobNumber ?? undefined,
          message: 'Offline job payload synchronised.',
          detail: {
            jobId: draft.jobId,
            contentMode,
            photoCount,
          },
        });
      }),
      catchError((error) => {
        this.audit.track({
          logSource: 'ONLINE',
          category: 'SYNC',
          action: AuditAction.SYNC_FAILED,
          severity: 'ERROR',
          outcome: 'FAILURE',
          entityType: 'Job',
          jobNumber: draft.jobNumber ?? undefined,
          message: 'Offline job payload failed to synchronise.',
          detail: { jobId: draft.jobId, contentMode, photoCount, error: error instanceof Error ? error.message : 'UnknownError' },
        });
        return throwError(() => error);
      }),
      // Closes the scope opened by syncResolvedJobToSqs so later events get their own ids.
      finalize(() => this.audit.endTransaction()),
    );
  }

  /** Distinguishes the data / photo / combined sync counts required by story 3579376. */
  private syncAction(contentMode: JobSyncContentMode, photoCount: number): string {
    if (contentMode === 'photos-only') return AuditAction.SYNC_PHOTO;
    if (contentMode === 'fields-only' || photoCount === 0) return AuditAction.SYNC_DATA;
    return AuditAction.SYNC_DATA_AND_PHOTO;
  }

  private applyLocalPhotoSyncPayload(jobId: number | string, localPayload: Record<string, unknown>): void {
    const photosByJob = localPayload['photos'];
    if (!photosByJob || typeof photosByJob !== 'object') return;

    const photos = (photosByJob as Record<string, unknown>)[String(jobId)];
    if (!Array.isArray(photos)) return;

    this.writeLocalPhotos(jobId, this.normalizePhotoList(photos, jobId));
  }

  private offlineSyncError<T = SyncOfflineDataResponse>(): Observable<T> | null {
    if (this.connectivity.currentOnline) return null;
    return throwError(() => new Error('Sync is available only when this device is online.'));
  }

  private validateOfflineIntegrity<T = SyncOfflineDataResponse>(): Observable<null> {
    return from(this.storage.validateIntegrity()).pipe(
      switchMap((valid) => {
        if (valid) return of(null);

        const tamperingDetails = this.storage.tamperingDetails();
        this.securityEvents.record('OFFLINE_SYNC_BLOCKED_TAMPER_DETECTED', tamperingDetails);
        this.audit.track({
          logSource: 'APP_LEVEL',
          category: 'SECURITY',
          action: AuditAction.SYNC_BLOCKED_TAMPER_DETECTED,
          severity: 'CRITICAL',
          outcome: 'FAILURE',
          authorizationDecision: 'DENY',
          entityType: 'IndexedDB',
          requestPath: '/api/v1/sync',
          message: 'Synchronisation blocked because offline data tampering was detected.',
          detail: tamperingDetails,
        });
        return throwError(() => new Error('Offline data integrity check failed. Re-authentication is required before syncing.'));
      }),
    );
  }

  private withActiveSession<T>(operation: () => Observable<T>): Observable<T> {
    return from(this.auth.ensureSessionForOperation()).pipe(
      switchMap((canContinue) => canContinue
        ? operation()
        : throwError(() => new Error('Sign-in is required before continuing.'))),
    );
  }

  private offlineDataSyncPayload(): Record<string, unknown> {
    const jobs = this.readLocalJobs();
    const propertyInfo: Record<string, PropertyInfo | null> = {};
    const photos: Record<string, PropertyPhoto[]> = {};
    jobs.forEach((job) => {
      const key = String(job.id);
      propertyInfo[key] = this.readLocalPropertyInfo(job.id);
      photos[key] = this.readLocalPhotos(job.id);
    });
    return {
      jobs,
      propertyInfo,
      photos,
      sync: {
        pendingJobIds: jobs.map((job) => String(job.id)),
        lastSyncedAt: null,
      },
    };
  }

  getSyncStatus(): Observable<SyncStatusResponse> {
    if (!this.useBackend()) return of({ pendingJobIds: [], pendingJobCount: 0, lastSyncedAt: null });

    return this.http.get<SyncStatusResponse>(`${API_BASE}/sync/status`).pipe(
      catchError(() => of({ pendingJobIds: [], pendingJobCount: 0, lastSyncedAt: null })),
    );
  }

  getReleaseNotes(): Observable<ReleaseNotes> {
    if (!this.useBackend()) return of(this.offlineReleaseNotes());

    return this.http.get<ReleaseNotes>(`${API_BASE}/release-notes`).pipe(
      catchError((error) => this.withLocalFallback(error, () => this.offlineReleaseNotes())),
    );
  }

  getAppInfo(): Observable<AppInfo> {
    if (!this.useBackend()) return of(this.offlineAppInfo());

    return this.http.get<AppInfo>(`${API_BASE}/app-info`).pipe(
      catchError((error) => this.withLocalFallback<AppInfo>(error, () => this.offlineAppInfo())),
    );
  }

  openFolder(folder: 'data' | 'logs'): Observable<{ ok: boolean; path: string }> {
    if (!this.useBackend()) return of({ ok: false, path: `${folder} is stored in the browser while offline` });

    return this.http.post<{ ok: boolean; path: string }>(`${API_BASE}/open-folder`, { folder }).pipe(
      catchError((error) => this.withLocalFallback(error, () => ({ ok: false, path: `${folder} is stored in the browser while offline` }))),
    );
  }

  private mapRemoteJobDetails(jobNumber: string, remote: Record<string, unknown>): RemoteJobDetails {
    const propertyDetails = this.remoteObject(remote, 'propertyDetails');
    const masterAddress = this.remoteObject(remote, 'masterAddress');
    const appointmentValue = this.remoteValue(remote, 'AppointmentDate', 'appointmentDate', 'ValuationDate', 'Valuation Date');
    const appointmentDate = this.formatRemoteDate(appointmentValue);
    const appointmentTime = this.formatRemoteTime(appointmentValue);
    const reportType = this.normalizeReportType(this.remoteString(remote, 'ReportType', 'reportType'));
    const mcProId = this.remoteValue(remote, 'mcProId', 'mcProID', 'MCProId', 'McProId');
    const valuationTypes = this.mapRemoteValuationTypes(remote['valuationTypes']);
    const propertyInfo = this.mapRemotePropertyInfo(remote, propertyDetails);
    propertyInfo.valuationTypes = valuationTypes;
    this.applyValuationTypeDescription(propertyInfo, valuationTypes);
    propertyInfo.buildings = JSON.stringify(this.remoteBuildings(remote, propertyDetails, propertyInfo));
    propertyInfo.otherBuildings = JSON.stringify(this.remoteOtherBuildings(remote, propertyDetails));


    return {
      raw: remote,
      job: {
        jobNumber,
        address: this.remoteString(masterAddress, 'completedStreetAddress')
          || this.remoteString(remote, 'CompletedStreetAddress', 'Address1')
          || `TOPS Job ${jobNumber}`,
        appointmentDate: appointmentDate || null,
        appointmentTime: appointmentTime || null,
        status: 'In Progress',
        reportType: reportType || null,
        mcProId: mcProId === null || mcProId === undefined || mcProId === '' ? null : mcProId as string | number,
      },
      propertyInfo,
      photos: this.mapRemotePhotos(remote, mcProId === null || mcProId === undefined || mcProId === '' ? null : mcProId as string | number),
      valuationTypes,
      imageList: this.mapRemoteImageList(remote['imageList']),
    };
  }

  private mapRemoteValuationTypes(value: unknown): ValuationTypeOption[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const source = item as Record<string, unknown>;
        const valuationTypeId = this.remoteString(source, 'valuationTypeId', 'ValuationTypeID', 'id', 'value');
        const valuationType = this.remoteString(source, 'valuationType', 'ValuationType', 'description', 'label');
        return valuationTypeId && valuationType ? { valuationTypeId, valuationType } : null;
      })
      .filter((item): item is ValuationTypeOption => !!item);
  }

  private applyValuationTypeDescription(propertyInfo: Partial<PropertyInfo>, valuationTypes: ValuationTypeOption[]): void {
    const selectedId = String(propertyInfo.valuationTypeId || propertyInfo.valuationType || '').trim();
    const selectedDescription = String(propertyInfo.valuationTypeDescription || '').trim();
    if (!selectedId && !selectedDescription) return;

    const selected = valuationTypes.find((option) =>
      String(option.valuationTypeId) === selectedId
      || String(option.valuationType) === selectedId
      || String(option.valuationType) === selectedDescription
    );
    if (!selected) return;

    propertyInfo.valuationType = selected.valuationTypeId;
    propertyInfo.valuationTypeId = selected.valuationTypeId;
    propertyInfo.valuationTypeDescription = selected.valuationType;
  }

  private mapAssignedRemoteJobSummary(remote: Record<string, unknown>): AssignedRemoteJobSummary {
    const appointmentValue = this.remoteValue(remote, 'appointmentDate', 'AppointmentDate', 'ValuationDate', 'Valuation Date');
    return {
      trackerId: Number(this.remoteValue(remote, 'trackerId', 'TrackerID') || 0),
      jobNumber: this.remoteString(remote, 'jobNumber', 'JobNumber') || null,
      address: this.remoteString(remote, 'address', 'CompletedStreetAddress') || `TOPS Job ${this.remoteString(remote, 'jobNumber', 'JobNumber') || ''}`,
      appointmentDate: this.formatRemoteDate(appointmentValue) || null,
      appointmentTime: this.formatRemoteTime(appointmentValue) || null,
      reportType: this.normalizeReportType(this.remoteString(remote, 'reportType', 'ReportType')) || null,
      client: this.remoteString(remote, 'client', 'Client') || null,
    };
  }

  private mapRemoteImageList(value: unknown): ImageListItem[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        if (typeof item === 'string') return { value: item, label: item };
        if (!item || typeof item !== 'object') return null;
        const source = item as Record<string, unknown>;
        const label = this.remoteString(source, 'label', 'Label', 'title', 'Title');
        const optionValue = this.remoteString(source, 'value', 'Value', 'id', 'ID') || label;
        return label ? { value: optionValue, label } : null;
      })
      .filter((item): item is ImageListItem => !!item);
  }

  private mapRemotePhotos(remote: Record<string, unknown>, mcProId: string | number | null): PropertyPhoto[] {
    const candidates = [
      remote['photos'],
      remote['photoUrls'],
      remote['images'],
      remote['imageUrls'],
      remote['propertyImages'],
      remote['inspectionImages'],
      remote['dwellingImages'],
      this.remoteObject(remote, 'propertyDetails')['photos'],
      this.remoteObject(remote, 'propertyDetails')['images'],
      this.remoteObject(remote, 'propertyDetails')['dwellingImages'],
    ];
    const source = candidates.find((value) => Array.isArray(value)) as unknown[] | undefined;
    if (!source) return [];

    return source.map((value, index) => {
      const photo = value && typeof value === 'object' ? value as Record<string, unknown> : {};
      const rawImage = typeof value === 'string'
        ? value
        : this.remotePhotoImageSource(photo);
      return {
        id: Number(photo['id'] ?? photo['photoId'] ?? photo['dwellingImageId'] ?? Date.now() + index),
        jobId: 0,
        photoUrl: this.normalizeImageSource(rawImage),
        label: this.importedPhotoLabel(photo, index),
        description: this.remoteNullableString(photo, 'description', 'Description'),
        imageType: this.remoteNullableString(photo, 'imageType', 'ImageType'),
        mcProId,
        documentUid: this.remoteNullableString(photo, 'documentUid', 'documentUID', 'DocumentUID'),
        md5Hash: this.remoteNullableString(photo, 'md5Hash', 'MD5Hash'),
        s3Bucket: this.remoteNullableString(photo, 's3Bucket', 'S3Bucket'),
        s3Key: this.remoteNullableString(photo, 's3Key', 'S3Key'),
        contentType: this.remoteNullableString(photo, 'contentType', 'imageType', 'ImageType'),
        fileExtension: this.remoteNullableString(photo, 'fileExtension', 'FileExtension'),
        displayOrder: this.normalizeDisplayOrder(photo['displayOrder'] ?? photo['DisplayOrder']),
      };
    }).filter((photo) => !!photo.photoUrl);
  }

  private remotePhotoImageSource(photo: Record<string, unknown>): unknown {
    return photo['presignedUrl']
      ?? photo['preSignedUrl']
      ?? photo['PresignedUrl']
      ?? photo['PreSignedUrl']
      ?? photo['presignedGetUrl']
      ?? photo['PresignedGetUrl']
      ?? photo['photoUrl']
      ?? photo['imageUrl']
      ?? photo['imageURL']
      ?? photo['ImageUrl']
      ?? photo['ImageURL']
      ?? photo['url']
      ?? photo['URL']
      ?? photo['image']
      ?? photo['imageData']
      ?? photo['fileUrl']
      ?? photo['FileUrl']
      ?? photo['documentUrl']
      ?? photo['documentURL']
      ?? photo['DocumentUrl']
      ?? photo['DocumentURL']
      ?? photo['downloadUrl']
      ?? photo['DownloadUrl']
      ?? photo['s3Url']
      ?? photo['S3Url'];
  }

  private importedPhotoLabel(photo: Record<string, unknown>, index: number): string {
      const label = photo['label'] ?? photo['imageName'] ?? photo['name'] ?? photo['fileName'] ?? photo['Description'];
    return typeof label === 'string' && label.trim() ? label.trim() : `Imported photo ${index + 1}`;
  }

  private remoteNullableString(source: Record<string, unknown>, ...keys: string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
  }

  private normalizeRemoteDrawingData(value: unknown): string | null {
    const source = this.normalizeImageSource(value);
    return source || null;
  }

  private remotePropertyNote(remote: Record<string, unknown>): Record<string, unknown> {
    const notes = remote['propertyNotes'];
    return Array.isArray(notes) && notes[0] && typeof notes[0] === 'object' ? notes[0] as Record<string, unknown> : {};
  }

  private mapRemotePropertyInfo(remote: Record<string, unknown>, propertyDetails: Record<string, unknown>): Partial<PropertyInfo> {
    const propertyNote = this.remotePropertyNote(remote);
    const clientInfo = this.remoteObject(remote, 'clientInfo');
    const propertyInfo: Partial<PropertyInfo> = {
      valuationType: this.remoteString(propertyDetails, 'valuationTypeId', 'TBE') || this.remoteString(remote, 'valuationTypeId', 'TBE'),
      valuationTypeId: this.remoteString(propertyDetails, 'valuationTypeId', 'TBE') || this.remoteString(remote, 'valuationTypeId', 'TBE'),
      valuationTypeDescription: this.remoteString(propertyDetails, 'valuationType') || this.remoteString(remote, 'valuationType'),
      propertyType: this.remoteString(propertyDetails, 'propertyType') || this.remoteString(remote, 'PropertyType'),
      mainWalls: this.remoteString(propertyDetails, 'mainWalls') || this.remoteString(remote, 'Main Walls'),
      levels: this.remoteString(propertyDetails, 'styleLevels') || this.remoteString(remote, 'StyleLevels'),
      windowFrames: this.remoteString(propertyDetails, 'windows') || this.remoteString(remote, 'Windows'),
      streetAppeal: this.remoteString(propertyDetails, 'streetAppeal') || this.remoteString(remote, 'StreetAppeal'),
      roof: this.remoteString(propertyDetails, 'roof') || this.remoteString(remote, 'Roof'),
      foundations: this.remoteString(propertyDetails, 'foundations') || this.remoteString(remote, 'Foundations'),
      interiorLinings: this.remoteString(propertyDetails, 'interiorLinings') || this.remoteString(remote, 'Interior Linings'),
      flooring: this.remoteString(propertyDetails, 'flooring') || this.remoteString(remote, 'Flooring'),
      ceilingLinings: this.remoteString(propertyDetails, 'ceilingLinings') || this.remoteString(remote, 'CeilingLinings'),
      bedrooms: this.remoteNumber(this.remoteValue(propertyDetails, 'accommodationBedrooms') ?? this.remoteValue(remote, 'AccommodationBedrooms')),
      bathrooms: this.remoteNumber(this.remoteValue(propertyDetails, 'accommodationBathrooms') ?? this.remoteValue(remote, 'AccommodartionBathrooms')),
      livingAreas: this.remoteNumber(this.remoteValue(propertyDetails, 'numberOfLivingAreas')),
      undercoverCarSpaces: this.remoteNumber(this.remoteValue(propertyDetails, 'undercoverCarSpaces')),
      fixturesAndFeatures: this.remoteString(propertyDetails, 'features') || this.remoteString(remote, 'Features'),
      accommodationPlus: this.remoteString(propertyDetails, 'accommodationPlus'),
      pcItems: this.remoteString(propertyDetails, 'pcFixtures'),
      clientNameContactDetails: this.remoteClientContactDetails(clientInfo) || this.remoteString(remote, 'client'),
      clientCompanyName: this.remoteString(clientInfo, 'companyName', 'CompanyName'),
      clientReportPreparedFor: this.remoteString(clientInfo, 'reportPreparedFor', 'ReportPreparedFor'),
      clientContactName: this.remoteString(clientInfo, 'contactName', 'ContactName'),
      clientPhone: this.remoteString(clientInfo, 'phone', 'Phone'),
      clientEmail: this.remoteString(clientInfo, 'email', 'Email'),
      neighbourhood: this.remoteString(propertyDetails, 'neighbourhood') || this.remoteString(remote, 'Neighbourhood'),
      services: this.remoteString(propertyDetails, 'services') || this.remoteString(remote, 'Services'),
      siteDescription: this.remoteString(propertyDetails, 'siteDescriptionTopography') || this.remoteString(remote, 'Site Description & Topography'),
      livingAreasSqm: this.remoteNumber(this.remoteValue(propertyDetails, 'livingAreas')),
      outdoorAreasSqm: this.remoteNumber(this.remoteValue(propertyDetails, 'outdoorAreas')),
      otherAreasSqm: this.remoteNumber(this.remoteValue(propertyDetails, 'otherAreas')),
      carAreasSqm: this.remoteNumber(this.remoteValue(propertyDetails, 'carAreas')),
      internalLayout: this.remoteString(propertyDetails, 'interiorLayout') || this.remoteString(remote, 'Interior Layout'),
      internalCondition: this.remoteString(propertyDetails, 'internalCondition') || this.remoteString(remote, 'Internal Condition'),
      externalCondition: this.remoteString(propertyDetails, 'externalCondition') || this.remoteString(remote, 'External Condition'),
      mainCarAccomType: this.remoteString(propertyDetails, 'carAccom'),
      yearOfConstruction: this.remoteString(propertyDetails, 'yearBuilt') || this.remoteString(remote, 'YearBuilt'),
      yearBuilt: this.remoteString(propertyDetails, 'yearBuilt') || this.remoteString(remote, 'YearBuilt'),
      yearOfAdditions: this.remoteString(propertyDetails, 'yearBuiltIndicator', 'yearModified', 'yearModifiedIndicator') || this.remoteString(remote, 'YearModified'),
      outdoorArea: this.remoteString(propertyDetails, 'outdoorArea'),
      significantImprovements: this.remoteString(propertyDetails, 'significantAncillary') || this.remoteString(remote, 'SignificantAncillary'),
      secondaryImprovements: this.remoteString(propertyDetails, 'secondaryAncillary') || this.remoteString(remote, 'SecondaryAncillary'),
      essentialRepairs: this.remoteRepairsValue(propertyDetails),
      rural: this.remoteRuralInfo(remote, propertyDetails),
      notes: this.remoteString(propertyNote, 'notes', 'Notes', 'inspectionNotes', 'inspectionNote', 'comments')
        || this.remoteString(propertyDetails, 'notes', 'Notes', 'inspectionNotes', 'inspectionNote', 'comments')
        || this.remoteString(remote, 'notes', 'Notes', 'inspectionNotes', 'inspectionNote', 'comments'),
      drawingData: this.normalizeRemoteDrawingData(
        this.remoteValue(propertyNote, 'drawingData', 'drawing', 'inspectionDrawing', 'sketch', 'diagram')
          ?? this.remoteValue(propertyDetails, 'drawingData', 'drawing', 'inspectionDrawing', 'sketch', 'diagram')
          ?? this.remoteValue(remote, 'drawingData', 'drawing', 'inspectionDrawing', 'sketch', 'diagram'),
      ),
    };
    return propertyInfo;
  }

  private remoteClientContactDetails(clientInfo: Record<string, unknown>): string {
    return [
      ['Company Name', this.remoteString(clientInfo, 'companyName', 'CompanyName')],
      ['Report Prepared For', this.remoteString(clientInfo, 'reportPreparedFor', 'ReportPreparedFor')],
      ['Contact Name', this.remoteString(clientInfo, 'contactName', 'ContactName')],
      ['Phone', this.remoteString(clientInfo, 'phone', 'Phone')],
      ['Email', this.remoteString(clientInfo, 'email', 'Email')],
    ]
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}`)
      .join('\n');
  }

  private remoteBuildings(remote: Record<string, unknown>, propertyDetails: Record<string, unknown>, propertyInfo: Partial<PropertyInfo>) {
    const remoteBuildings = this.firstRemoteArray(
      remote['multiDwelling'],
      remote['multiDwellings'],
      remote['buildings'],
      remote['dwellingBuildings'],
      propertyDetails['multiDwelling'],
      propertyDetails['multiDwellings'],
      propertyDetails['buildings'],
      propertyDetails['dwellingBuildings'],
    );
    const mainDwelling = this.remoteBuilding(propertyInfo, {
      ...propertyDetails,
      name: 'Main Dwelling',
      levels: propertyDetails['styleLevels'] ?? propertyDetails['levels'] ?? propertyInfo.levels,
      style: propertyDetails['styleAttached'] ?? propertyDetails['style'],
      windowFrames: propertyDetails['windows'] ?? propertyDetails['windowFrames'],
      bedrooms: propertyDetails['accommodationBedrooms'] ?? propertyDetails['bedrooms'],
      bathrooms: propertyDetails['accommodationBathrooms'] ?? propertyDetails['bathrooms'],
      numberOfLivingAreas: propertyDetails['numberOfLivingAreas'] ?? propertyDetails['livingAreas'],
      carAccommodation: propertyDetails['carAccom'] ?? propertyDetails['carAccommodation'],
      pcItems: propertyDetails['accommodationPlus'] ?? propertyDetails['pcItems'] ?? propertyDetails['pcFixtures'],
      internalLayout: propertyDetails['interiorLayout'] ?? propertyDetails['internalLayout'],
      yearOfConstruction: propertyDetails['builtAbout'] ?? propertyDetails['yearBuilt'],
      yearOfAdditions: propertyDetails['additionsAbout'] ?? propertyDetails['yearOfAdditions'],
      livingAreasSqm: propertyDetails['livingAreas'] ?? propertyDetails['livingAreasSqm'],
      outdoorAreasSqm: propertyDetails['outdoorAreas'] ?? propertyDetails['outdoorAreasSqm'],
      otherAreasSqm: propertyDetails['otherAreas'] ?? propertyDetails['otherAreasSqm'],
      carAreasSqm: propertyDetails['carAreas'] ?? propertyDetails['carAreasSqm'],
      floorAreas: propertyDetails['floorAreas'],
      significantImprovements: propertyDetails['significantAncillary'] ?? propertyDetails['significantImprovements'],
      secondaryImprovements: propertyDetails['secondaryAncillary'] ?? propertyDetails['secondaryImprovements'],
      carSpaces: propertyDetails['carNumber'],
    }, 1);
    if (remoteBuildings.length) {
      return [
        mainDwelling,
        ...remoteBuildings.map((item, index) => this.remoteBuilding(propertyInfo, this.remoteRecord(item), index + 2)),
      ];
    }
    return [mainDwelling];
  }

  private remoteRuralInfo(remote: Record<string, unknown>, propertyDetails: Record<string, unknown>): RuralInfo {
    const remoteRural = this.remoteObject(remote, 'rural');
    const propertyRural = this.remoteObject(propertyDetails, 'rural');
    const rural = Object.keys(remoteRural).length ? remoteRural : propertyRural;
    return {
      water: this.remoteString(rural, 'water'),
      drainage: this.remoteString(rural, 'drainage'),
      irrigation: this.remoteString(rural, 'irrigation'),
      fencing: this.remoteString(rural, 'fencing'),
      races: this.remoteString(rural, 'races'),
      essentialRepairRequired: this.remoteString(rural, 'essentialRepairRequired'),
      essentialRepairComments: this.remoteString(rural, 'essentialRepairComments'),
    };
  }

  private remoteBuilding(propertyInfo: Partial<PropertyInfo>, building: Record<string, unknown>, index = 1) {
    return {
    ...emptyBuilding(index),
    
    multiDwellingImprovementId: building['multiDwellingImprovementId'] ,
    isDelete: building['isDelete'] || false,

    name: building['name'] ?? '',
    levels: building['levels'] ?? '',
    style: building['style'] ?? '',

    streetAppeal: building['streetAppeal'] ?? '',
    foundations: building['foundations'] ?? '',
    flooring: building['flooring'] ?? '',
    mainWalls: building['mainWalls'] ?? '',
    windowFrames: building['windowFrames'] ?? '',
    roof: building['roof'] ?? '',

    interiorLinings: building['interiorLinings'] ?? '',
    ceilingLinings: building['ceilingLinings'] ?? '',
    frame: building['frame'] ?? '',

    bedrooms: building['bedrooms'],
    bathrooms: building['bathrooms'],
    numberOfLivingAreas: building['numberOfLivingAreas'] ?? building['livingAreas'] ?? '',

    undercoverCarSpaces: building['undercoverCarSpaces'] ?? '',
    carAccommodation: building['carAccommodation'] ?? building['carAccom'] ?? '',
    pcItems: building['pcItems'] ?? building['accommodationPlus'] ?? '',
    features: building['features'] ?? '',
    chattels: building['chattels'] ?? '',

    briefDescription: building['briefDescription'] ?? '',

    internalLayout: building['internalLayout'] ?? '',
    internalCondition: building['internalCondition'] ?? '',
    externalCondition: building['externalCondition'] ?? '',

    buildingCondition: building['buildingCondition'] ?? '',
    earthquakeClause: building['earthquakeClause'] ?? '',

    yearBuilt: building['yearBuilt'],
    yearBuiltIndicator: building['yearBuiltIndicator'],
    yearModified: building['yearModified'],
    yearModifiedIndicator: building['yearModifiedIndicator'],

    yearOfConstruction: building['yearOfConstruction'] ?? '',
    yearOfAdditions: building['yearOfAdditions'] ?? '',
    proposedAlterAddition: building['proposedAlterAddition'] ?? '',
    significantImprovements: building['significantImprovements'] ?? building['significantAncillary'] ?? propertyInfo.significantImprovements ?? '',
    secondaryImprovements: building['secondaryImprovements'] ?? building['secondaryAncillary'] ?? propertyInfo.secondaryImprovements ?? '',

    livingAreasSqm: building['livingAreasSqm'],
    outdoorAreasSqm: building['outdoorAreasSqm'],
    carAreasSqm: building['carAreasSqm'],
    otherAreasSqm: building['otherAreasSqm'],
    floorAreas: Array.isArray(building['floorAreas']) ? building['floorAreas'] : [],
    carSpaces: building['carSpaces'] ?? building['carNumber'] ?? null,

    outdoorArea: building['outdoorArea'] ?? '',

    totalFloorArea: building['totalFloorArea'],
    totalBuildingArea: building['totalBuildingArea'],

    floorAreasMeasurement: building['floorAreasMeasurement'] ?? '',
    multipleFloorAreas: building['multipleFloorAreas'] ?? '',

    constructionRisk: building['constructionRisk'] ?? '',

    marketRental: building['marketRental'],
    actualRent: building['actualRent'],

    actualRentLabel: building['actualRentLabel'] ?? '',
    actualRentCommenced: building['actualRentCommenced'] ?? '',
    actualRentUntil: building['actualRentUntil'] ?? '',

    isNotSuitableForRent: building['isNotSuitableForRent'] ?? false,
    notSuitableForRentComment: building['notSuitableForRentComment'] ?? '',

    power: building['power'] ?? '0',
    sewerage: building['sewerage'] ?? '0',
    townWater: building['townWater'] ?? '0',
    tankWater: building['tankWater'] ?? '0',
    gas: building['gas'] ?? '0',
    lpGas: building['lpGas'] ?? '0',
    telephone: building['telephone'] ?? '0'

    };
  }

  private remoteOtherBuildings(remote: Record<string, unknown>, propertyDetails: Record<string, unknown>): OtherBuilding[] {
    const remoteOtherBuildings = this.firstRemoteArray(remote['otherBuildings'], propertyDetails['otherBuildings']);
    return remoteOtherBuildings.map((item, index) => {
      const building = this.remoteRecord(item);
      return {
        id: `other-building-${Date.now()}-${index + 1}`,
        title: this.remoteString(building, 'title'),
        area: this.normalizeAreaDisplayValue(this.remoteString(building, 'area')),
        foundations: this.remoteString(building, 'foundations'),
        flooring: this.remoteString(building, 'flooring'),
        walls: this.remoteString(building, 'walls'),
        roof: this.remoteString(building, 'roof'),
        pcItems: this.remoteString(building, 'pcItems'),
        notes: this.remoteString(building, 'notes'),
      };
    });
  }

  private firstRemoteArray(...values: unknown[]): unknown[] {
    return values.find((value): value is unknown[] => Array.isArray(value) && value.length > 0) || [];
  }

  private remoteRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  }

  private formatRemoteDate(value: unknown): string {
    if (!value) return '';
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${date.getFullYear()}`;
  }

  private formatRemoteTime(value: unknown): string {
    if (!value) return '';
    const text = String(value);
    const timeMatch = text.match(/[T\s](\d{1,2}:\d{2})(?::\d{2})?/);
    if (timeMatch) return timeMatch[1].padStart(5, '0');
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private normalizeReportType(value: string): string {
    const reportTypes = this.mcproReportTypesCache().map((option) => option.reportType).filter((reportType): reportType is string => !!reportType);
    const matched = reportTypes.find((reportType) => value.toLowerCase().includes(reportType.toLowerCase()) || reportType.toLowerCase().includes(value.toLowerCase()));

    return matched || value;
  }

  private remoteRepairsValue(propertyDetails: Record<string, unknown>): string {
    const essentialRepairs = this.remoteString(propertyDetails, 'essentialRepairs');
    if (essentialRepairs) return essentialRepairs;
    const repairs = this.remoteString(propertyDetails, 'repairs');
    if (/^yes$/i.test(repairs)) return 'Yes';
    if (/^no$/i.test(repairs)) return 'No';
    return '';
  }

  private remoteObject(source: Record<string, unknown>, key: string): Record<string, unknown> {
    const value = source[key];
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
  }

  private remoteString(source: Record<string, unknown>, ...keys: string[]): string {
    const value = this.remoteValue(source, ...keys);
    return value === null || value === undefined ? '' : String(value);
  }

  private remoteValue(source: Record<string, unknown>, ...keys: string[]): unknown {
    for (const key of keys) {
      const value = source[key];
      if (value !== null && value !== undefined && value !== '') return value;
    }
    return null;
  }

  private remoteNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private useBackend(): boolean {
    return false;
  }

  private localJob(id: number | string): Observable<Job> {
    return this.withStorageReady(() => this.localJobValue(id));
  }

  private localJobValue(id: number | string): Job {
    const job = this.readLocalJobs().find((item) => String(item.id) === String(id));
    if (!job) throw new Error('Job not found.');
    return job;
  }

  private offlineReleaseNotes(): ReleaseNotes {
    return { version: 'Offline', changes: [] };
  }

  private offlineAppInfo(): AppInfo {
    return {
      version: 'Offline',
      dataDir: 'Browser IndexedDB',
      logsDir: 'Not available offline',
      storageBackend: 'indexeddb',
      isDesktop: false,
      platform: navigator.platform || 'browser',
    };
  }

  private withStorageReady<T>(factory: () => T): Observable<T> {
    return from(this.storage.ready()).pipe(map(() => factory()));
  }

  private withLocalFallback<T>(error: unknown, fallback: () => T): Observable<T> {
    if (!this.isBackendUnavailable(error)) {
      return throwError(() => error);
    }

    return from(this.storage.ready()).pipe(
      map(() => fallback()),
      catchError((fallbackError) => throwError(() => fallbackError)),
    );
  }

  private isBackendUnavailable(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    const body = typeof error.error === 'string' ? error.error : '';
    return error.status === 0
      || (error.status === 500 && body.includes('ECONNREFUSED'));
  }

  private readLocalJobs(): Job[] {
    const stored = this.readStorage();
    if (!stored) return this.withRecoveredLocalJobs([...this.memoryJobs]);

    try {
      const jobs = JSON.parse(stored) as Job[];
      this.memoryJobs = Array.isArray(jobs) ? jobs : [];
    } catch {
      this.memoryJobs = [];
    }

    return this.withRecoveredLocalJobs([...this.memoryJobs]);
  }

  private withRecoveredLocalJobs(jobs: Job[]): Job[] {
    const existingJobIds = new Set(jobs.map((job) => String(job.id)));
    const recoveredJobs = this.storageKeys()
      .filter((key) => key.startsWith(this.localPropertyInfoPrefix))
      .map((key) => key.slice(this.localPropertyInfoPrefix.length))
      .filter((jobId) => jobId && !existingJobIds.has(jobId))
      .map((jobId) => this.recoveredLocalJob(jobId));
    const mergedJobs = [...jobs, ...recoveredJobs];
    if (recoveredJobs.length) this.writeLocalJobs(mergedJobs);
    return mergedJobs;
  }

  private recoveredLocalJob(jobId: string): Job {
    const propertyInfo = this.readLocalPropertyInfo(jobId) as (PropertyInfo & Record<string, unknown>) | null;
    return {
      id: Number(jobId) || 0,
      jobNumber: String(propertyInfo?.['jobNumber'] || `Recovered-${jobId}`),
      address: String(propertyInfo?.['address'] || `Recovered offline job ${jobId}`),
      appointmentDate: propertyInfo?.['appointmentDate'] as string | null || null,
      appointmentTime: propertyInfo?.['appointmentTime'] as string | null || null,
      status: propertyInfo?.['status'] as string | null || 'In Progress',
      photoUrl: propertyInfo?.['photoUrl'] as string | null || null,
      reportType: propertyInfo?.['reportType'] as string | null || null,
      mcProId: propertyInfo?.['mcProId'] as string | number | null || null,
    };
  }

  private createLocalJob(payload: JobPayload): Job {
    const address = payload.address?.trim();
    if (!address) throw new Error('Address is required.');
    const jobs = this.readLocalJobs();
    const job: Job = {
      id: this.nextLocalJobId(jobs),
      jobNumber: payload.jobNumber || null,
      address,
      appointmentDate: payload.appointmentDate || null,
      appointmentTime: payload.appointmentTime || null,
      status: payload.status || 'In Progress',
      photoUrl: payload.photoUrl || null,
      reportType: payload.reportType || null,
      mcProId: payload.mcProId ?? null,
    };
    this.writeLocalJobs([job, ...jobs]);
    return job;
  }

  private updateLocalJob(id: number | string, payload: Partial<JobPayload>, originalError: unknown): Job {
    const jobs = this.readLocalJobs();
    const index = jobs.findIndex((job) => String(job.id) === String(id));
    if (index < 0) throw originalError;
    if (payload.address !== undefined && !payload.address.trim()) throw new Error('Address is required.');

    const updated = { ...jobs[index], ...payload, address: payload.address === undefined ? jobs[index].address : payload.address.trim() };
    jobs[index] = updated;
    this.writeLocalJobs(jobs);
    return updated;
  }

  private deleteLocalJob(id: number | string): void {
    this.writeLocalJobs(this.readLocalJobs().filter((job) => String(job.id) !== String(id)));
    this.removeStorage(this.propertyInfoKey(id));
    this.removeStorage(this.photosKey(id));
  }

  private readLocalPropertyInfo(jobId: number | string): PropertyInfo | null {
    const stored = this.readStorage(this.propertyInfoKey(jobId));
    if (!stored) return null;

    try {
      return JSON.parse(stored) as PropertyInfo;
    } catch {
      return null;
    }
  }

  private writeLocalPropertyInfo(jobId: number | string, payload: PropertyInfo): PropertyInfo {
    const propertyInfo = { ...payload, jobId: Number(jobId) || payload.jobId };
    this.writeStorage(this.propertyInfoKey(jobId), JSON.stringify(propertyInfo));
    return propertyInfo;
  }

  private readLocalPhotos(jobId: number | string): PropertyPhoto[] {
    const stored = this.readStorage(this.photosKey(jobId));
    if (!stored) return [];

    try {
      const photos = JSON.parse(stored) as PropertyPhoto[];
      return Array.isArray(photos) ? photos : [];
    } catch {
      return [];
    }
  }

  private readLocalImageList(): ImageListItem[] {
    const stored = this.readStorage(this.localImageListKey);
    if (!stored) return [];

    try {
      const imageList = JSON.parse(stored) as ImageListItem[];
      return Array.isArray(imageList) ? imageList : [];
    } catch {
      return [];
    }
  }

  private writeLocalImageList(imageList: ImageListItem[]): void {
    this.writeStorage(this.localImageListKey, JSON.stringify(imageList));
  }

  private localJobSyncRecord(jobId: number | string): Observable<JobSyncRecord> {
    return forkJoin({
      job: this.getJob(jobId),
      propertyInfo: this.getPropertyInfo(jobId),
      photos: this.getPhotos(jobId),
    });
  }

  private serverJobSyncRecord(localJobId: number | string, details: RemoteJobDetails): JobSyncRecord {
    return {
      job: {
        ...details.job,
        id: Number(localJobId) || 0,
      } as Job,
      propertyInfo: {
        ...details.propertyInfo,
        jobId: Number(localJobId) || 0,
      },
      photos: details.photos,
    };
  }

  private buildJobSyncConflicts(server: JobSyncRecord, local: JobSyncRecord): JobSyncConflict[] {
    const serverValues = this.flattenComparableRecord(this.comparableSyncRecord(server));
    const localValues = this.flattenComparableRecord(this.comparableSyncRecord(local));
    const paths = [...new Set([...Object.keys(serverValues), ...Object.keys(localValues)])].sort();
    return paths
      .filter((path) => this.shouldCompareSyncPath(path, server, local))
      .filter((path) => !this.isBlankSyncValue(serverValues[path]))
      .filter((path) => this.stableValue(serverValues[path]) !== this.stableValue(localValues[path]))
      .map((path) => ({
        path,
        ...this.syncConflictLabels(path, server, local),
        serverValue: serverValues[path] ?? null,
        localValue: localValues[path] ?? null,
        valueKind: this.syncConflictValueKind(path, serverValues[path], localValues[path]),
        selected: 'local' as const,
        customValue: '',
      }));
  }

  private comparableSyncRecord(record: JobSyncRecord): Record<string, unknown> {
    return {
      propertyInfo: this.comparablePropertyInfo(record.propertyInfo || {}),
    };
  }

  private comparablePropertyInfo(propertyInfo: Partial<PropertyInfo>): Record<string, unknown> {
    const excludedFields = new Set(['id', 'jobId', 'mcProId']);
    const comparable: Record<string, unknown> = {};
    Object.entries(propertyInfo as Record<string, unknown>).forEach(([key, value]) => {
      if (excludedFields.has(key)) return;
      if (key === 'buildings' || key === 'otherBuildings') {
        comparable[key] = this.parseComparableArray(value).map((item) => this.comparableJsonArrayItem(key, item));
        return;
      }
      comparable[key] = value;
    });
    return comparable;
  }

  private comparableJsonArrayItem(arrayField: string, item: unknown): Record<string, unknown> {
    const allowedFields = this.syncAllowedJsonArrayFields(arrayField);
    const source = item && typeof item === 'object' ? item as Record<string, unknown> : {};
    return Object.entries(source).reduce<Record<string, unknown>>((result, [key, value]) => {
      if (allowedFields.has(key)) result[key] = arrayField === 'otherBuildings' && key === 'area'
        ? this.normalizeAreaDisplayValue(value)
        : value;
      return result;
    }, {});
  }

  private normalizeAreaDisplayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '0.00';
    const numberValue = Number(String(value).replace(/,/g, '').trim());
    return Number.isFinite(numberValue) ? Math.max(0, numberValue).toFixed(2) : String(value);
  }

  private syncAllowedJsonArrayFields(arrayField: string): Set<string> {
    if (arrayField === 'buildings') {
      return new Set([
        'name',
        'levels',
        'mainWalls',
        'style',
        'windowFrames',
        'streetAppeal',
        'roof',
        'foundations',
        'interiorLinings',
        'flooring',
        'ceilingLinings',
        'frame',
        'bedrooms',
        'bathrooms',
        'numberOfLivingAreas',
        'undercoverCarSpaces',
        'carSpaces',
        'carAccommodation',
        'pcItems',
        'features',
        'chattels',
        'briefDescription',
        'livingAreasSqm',
        'outdoorAreasSqm',
        'otherAreasSqm',
        'carAreasSqm',
        'internalLayout',
        'internalCondition',
        'externalCondition',
        'yearOfConstruction',
        'yearOfAdditions',
        'outdoorArea',
        'significantImprovements',
        'secondaryImprovements',
      ]);
    }
    if (arrayField === 'otherBuildings') {
      return new Set(['title', 'area', 'foundations', 'flooring', 'walls', 'roof', 'pcItems', 'notes']);
    }
    return new Set();
  }

  private shouldCompareSyncPath(path: string, server: JobSyncRecord, local: JobSyncRecord): boolean {
    const parts = path.split('.');
    if (parts[0] !== 'propertyInfo') return false;
    if (parts[1] === 'notes' || parts[1] === 'drawingData') return false;
    if (parts[1] === 'rural') return parts.length > 2 && this.syncAllowedRuralFields().has(parts[2]);
    if (parts[1] !== 'buildings' && parts[1] !== 'otherBuildings') return false;

    const index = Number(parts[2]);
    if (!Number.isInteger(index) || parts.length < 4) return false;
    return this.hasComparableJsonArrayItem(server.propertyInfo, local.propertyInfo, parts[1], index);
  }

  private syncAllowedRuralFields(): Set<string> {
    return new Set(['water', 'drainage', 'irrigation', 'fencing', 'races', 'essentialRepairRequired', 'essentialRepairComments']);
  }

  private hasComparableJsonArrayItem(serverPropertyInfo: Partial<PropertyInfo> | null, localPropertyInfo: Partial<PropertyInfo> | null, arrayField: string, index: number): boolean {
    const serverItem = this.syncArrayItem(serverPropertyInfo, arrayField, index);
    const localItem = this.syncArrayItem(localPropertyInfo, arrayField, index);
    if (!serverItem || !localItem) return false;
    if (arrayField === 'otherBuildings') return true;
    if (arrayField === 'buildings' && (this.isDeletedDwelling(serverItem) || this.isDeletedDwelling(localItem))) return false;

    const serverId = this.syncArrayStableId(arrayField, serverItem);
    const localId = this.syncArrayStableId(arrayField, localItem);
    if (serverId && localId) return serverId === localId;
    if (arrayField === 'buildings' && (serverId || localId)) return false;
    return true;
  }

  private syncArrayItem(propertyInfo: Partial<PropertyInfo> | null, arrayField: string, index: number): Record<string, unknown> | null {
    const source = propertyInfo as Record<string, unknown> | null;
    const item = this.parseComparableArray(source?.[arrayField])[index];
    return item && typeof item === 'object' ? item as Record<string, unknown> : null;
  }

  private syncArrayStableId(arrayField: string, item: Record<string, unknown>): string {
    const value = arrayField === 'buildings' ? item['multiDwellingImprovementId'] : null;
    return value === null || value === undefined || value === '' ? '' : String(value);
  }

  private isDeletedDwelling(item: Record<string, unknown>): boolean {
    return item['isDelete'] === true || item['isDelete'] === 'true';
  }

  private parseComparableArray(value: unknown): unknown[] {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private flattenComparableRecord(value: unknown, prefix = '', result: Record<string, unknown> = {}): Record<string, unknown> {
    if (Array.isArray(value)) {
      value.forEach((item, index) => this.flattenComparableRecord(item, prefix ? `${prefix}.${index}` : String(index), result));
      return result;
    }
    if (!value || typeof value !== 'object') {
      result[prefix || 'value'] = value ?? null;
      return result;
    }
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (Array.isArray(item)) {
        this.flattenComparableRecord(item, path, result);
      } else if (!item || typeof item !== 'object') {
        result[path] = item ?? null;
      } else {
        this.flattenComparableRecord(item, path, result);
      }
    });
    return result;
  }

  private stableValue(value: unknown): string {
    if (!value || typeof value !== 'object') return String(value ?? '');
    if (Array.isArray(value)) return `[${value.map((item) => this.stableValue(item)).join(',')}]`;
    return `{${Object.keys(value as Record<string, unknown>).sort().map((key) => `${key}:${this.stableValue((value as Record<string, unknown>)[key])}`).join(',')}}`;
  }

  private isBlankSyncValue(value: unknown): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  }

  private syncConflictLabels(path: string, server: JobSyncRecord, local: JobSyncRecord): { section: string; label: string; fieldLabel: string } {
    const parts = path.split('.');
    if (parts[0] === 'propertyInfo' && parts[1] === 'buildings') {
      const dwellingNumber = Number.isFinite(Number(parts[2])) ? Number(parts[2]) + 1 : 1;
      const fieldLabel = this.syncFieldLabel(parts[3]);
      const section = this.syncArrayDisplayName(local.propertyInfo, server.propertyInfo, 'buildings', dwellingNumber - 1, 'name', `Dwelling ${dwellingNumber}`);
      return { section, label: `${section} - ${fieldLabel}`, fieldLabel };
    }
    if (parts[0] === 'propertyInfo' && parts[1] === 'otherBuildings') {
      const buildingNumber = Number.isFinite(Number(parts[2])) ? Number(parts[2]) + 1 : 1;
      const fieldLabel = this.syncFieldLabel(parts[3]);
      const section = this.syncArrayDisplayName(local.propertyInfo, server.propertyInfo, 'otherBuildings', buildingNumber - 1, 'title', `Other Building ${buildingNumber}`);
      return { section, label: `${section} - ${fieldLabel}`, fieldLabel };
    }
    if (parts[0] === 'propertyInfo' && parts[1] === 'rural') {
      const fieldLabel = this.syncFieldLabel(parts[2]);
      const section = 'Property Services and Improvements';
      return { section, label: `${section} - ${fieldLabel}`, fieldLabel };
    }
    const fieldLabel = this.readableFieldLabel(parts.join(' '));
    return { section: 'Improvements', label: `Improvements - ${fieldLabel}`, fieldLabel };
  }

  private syncFieldLabel(key: string): string {
    const labels: Record<string, string> = {
      name: 'Dwelling Name',
      levels: 'Levels',
      mainWalls: 'Main Walls',
      style: 'Style',
      windowFrames: 'Window Frames',
      streetAppeal: 'Street Appeal',
      roof: 'Roof',
      foundations: 'Foundations',
      interiorLinings: 'Interior Linings',
      flooring: 'Flooring',
      ceilingLinings: 'Ceiling Linings',
      frame: 'Frame',
      bedrooms: 'No. of Bedrooms',
      bathrooms: 'No. of Bathrooms',
      numberOfLivingAreas: 'Number of Living Areas',
      undercoverCarSpaces: 'No. of Undercover Car Spaces',
      carAccommodation: 'Car Accommodation',
      pcItems: 'PC Items',
      features: 'Additional Features',
      chattels: 'Chattels',
      briefDescription: 'Improvements Overview',
      livingAreasSqm: 'Living Areas (m2)',
      outdoorAreasSqm: 'Outdoor Areas (m2)',
      otherAreasSqm: 'Other Areas (m2)',
      carAreasSqm: 'Car Areas (m2)',
      internalLayout: 'Internal Layout',
      internalCondition: 'Internal Condition',
      externalCondition: 'External Condition',
      yearOfConstruction: 'Year of Construction',
      yearOfAdditions: 'Year Most Recent Additions Constructed',
      outdoorArea: 'Outdoor Area',
      significantImprovements: 'Other Improvements (Significant Ancillary)',
      secondaryImprovements: 'Other Improvements (Secondary Ancillary)',
      title: 'Title',
      area: 'Area (m2)',
      walls: 'Walls',
      notes: 'Notes',
      water: 'Water',
      drainage: 'Drainage',
      irrigation: 'Irrigation',
      fencing: 'Fencing',
      races: 'Races',
      essentialRepairRequired: 'Essential Repair?',
      essentialRepairComments: 'Essential Repair Comments',
    };
    return labels[key] || this.readableFieldLabel(key);
  }

  private syncArrayDisplayName(
    localPropertyInfo: Partial<PropertyInfo> | null,
    serverPropertyInfo: Partial<PropertyInfo> | null,
    arrayField: string,
    index: number,
    nameField: string,
    fallback: string,
  ): string {
    const localName = this.syncArrayItemName(localPropertyInfo, arrayField, index, nameField);
    const serverName = this.syncArrayItemName(serverPropertyInfo, arrayField, index, nameField);
    return localName || serverName || fallback;
  }

  private syncArrayItemName(propertyInfo: Partial<PropertyInfo> | null, arrayField: string, index: number, nameField: string): string {
    const value = this.syncArrayItem(propertyInfo, arrayField, index)?.[nameField];
    return value === null || value === undefined ? '' : String(value).trim();
  }

  private readableFieldLabel(value: string): string {
    return value
      .replace(/\d+/g, '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Value';
  }

  private syncConflictValueKind(path: string, serverValue: unknown, localValue: unknown): 'text' | 'image' {
    if (/photoUrl|drawingData/i.test(path)) return 'image';
    return [serverValue, localValue].some((value) => typeof value === 'string' && value.startsWith('data:image/')) ? 'image' : 'text';
  }

  private applySyncSelections(local: JobSyncRecord, server: JobSyncRecord, conflicts: JobSyncConflict[]): JobSyncRecord {
    const resolved = this.cloneSyncRecord(local);
    conflicts.forEach((conflict) => {
      const customValue = this.customSyncConflictValue(conflict);
      if (customValue !== null) {
        this.setConflictCustomValue(resolved, conflict.path, customValue);
        return;
      }
      if (conflict.selected !== 'server') return;
      if (conflict.path.startsWith('photos.')) {
        this.setPhotoConflictValue(resolved, server, conflict.path);
        return;
      }
      if (conflict.path.startsWith('propertyInfo.')) this.setPropertyInfoConflictValue(resolved, server, conflict.path);
    });
    return resolved;
  }

  private customSyncConflictValue(conflict: JobSyncConflict): unknown | null {
    if (conflict.customValue === undefined || conflict.customValue === '') return null;
    return conflict.customValue;
  }

  private setConflictCustomValue(target: JobSyncRecord, path: string, value: unknown): void {
    if (path.startsWith('photos.')) {
      const [, indexText, field] = path.split('.');
      const index = Number(indexText);
      if (!Number.isInteger(index) || !field) return;
      if (!target.photos[index]) return;
      (target.photos[index] as unknown as Record<string, unknown>)[field] = this.cloneValue(value);
      return;
    }
    if (path.startsWith('propertyInfo.')) this.setPropertyInfoCustomValue(target, path, value);
  }

  private cloneSyncRecord(record: JobSyncRecord): JobSyncRecord {
    return this.cloneValue(record) as JobSyncRecord;
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value ?? null)) as T;
  }

  private valueAtPath(source: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key) => current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined, source);
  }

  private setValueAtPath(target: unknown, path: string, value: unknown): void {
    const parts = path.split('.');
    let current = target as Record<string, unknown>;
    parts.slice(0, -1).forEach((part) => {
      if (!current[part] || typeof current[part] !== 'object') current[part] = {};
      current = current[part] as Record<string, unknown>;
    });
    current[parts[parts.length - 1]] = this.cloneValue(value);
  }

  private setPhotoConflictValue(target: JobSyncRecord, source: JobSyncRecord, path: string): void {
    const [, indexText, field] = path.split('.');
    const index = Number(indexText);
    if (!Number.isInteger(index) || !field) return;
    if (!target.photos[index]) target.photos[index] = this.cloneValue(source.photos[index]);
    if (!target.photos[index] || !source.photos[index]) return;
    (target.photos[index] as unknown as Record<string, unknown>)[field] = this.cloneValue((source.photos[index] as unknown as Record<string, unknown>)[field]);
  }

  private setPropertyInfoConflictValue(target: JobSyncRecord, source: JobSyncRecord, path: string): void {
    if (!target.propertyInfo) target.propertyInfo = {};
    const parts = path.split('.').slice(1);
    const sourceProperty = source.propertyInfo as Record<string, unknown> | null;
    if (!sourceProperty) return;
    if (parts[0] === 'buildings' || parts[0] === 'otherBuildings') {
      this.setJsonArrayPropertyConflictValue(target.propertyInfo as Record<string, unknown>, sourceProperty, parts);
      return;
    }
    this.setValueAtPath(target.propertyInfo, parts.join('.'), this.valueAtPath(sourceProperty, parts.join('.')));
  }

  private setPropertyInfoCustomValue(target: JobSyncRecord, path: string, value: unknown): void {
    if (!target.propertyInfo) target.propertyInfo = {};
    const parts = path.split('.').slice(1);
    if (parts[0] === 'buildings' || parts[0] === 'otherBuildings') {
      this.setJsonArrayPropertyCustomValue(target.propertyInfo as Record<string, unknown>, parts, value);
      return;
    }
    this.setValueAtPath(target.propertyInfo, parts.join('.'), this.cloneValue(value));
  }

  private setJsonArrayPropertyConflictValue(targetProperty: Record<string, unknown>, sourceProperty: Record<string, unknown>, parts: string[]): void {
    const [arrayField, indexText, ...fieldParts] = parts;
    const index = Number(indexText);
    const field = fieldParts.join('.');
    if (!arrayField || !Number.isInteger(index) || !field) return;
    const targetArray = this.parseComparableArray(targetProperty[arrayField]).map((item) => item && typeof item === 'object' ? item as Record<string, unknown> : {});
    const sourceArray = this.parseComparableArray(sourceProperty[arrayField]).map((item) => item && typeof item === 'object' ? item as Record<string, unknown> : {});
    if (!targetArray[index]) targetArray[index] = {};
    this.setValueAtPath(targetArray[index], field, this.valueAtPath(sourceArray[index], field));
    targetProperty[arrayField] = JSON.stringify(targetArray);
  }

  private setJsonArrayPropertyCustomValue(targetProperty: Record<string, unknown>, parts: string[], value: unknown): void {
    const [arrayField, indexText, ...fieldParts] = parts;
    const index = Number(indexText);
    const field = fieldParts.join('.');
    if (!arrayField || !Number.isInteger(index) || !field) return;
    const targetArray = this.parseComparableArray(targetProperty[arrayField]).map((item) => item && typeof item === 'object' ? item as Record<string, unknown> : {});
    if (!targetArray[index]) targetArray[index] = {};
    this.setValueAtPath(targetArray[index], field, this.cloneValue(value));
    targetProperty[arrayField] = JSON.stringify(targetArray);
  }

  private jobSyncPayload(jobId: number | string, record: JobSyncRecord): Record<string, unknown> {
    const key = String(jobId);
    return {
      jobs: [{ ...record.job, id: Number(jobId) || jobId }],
      propertyInfo: { [key]: this.syncPropertyInfo(jobId, record.propertyInfo) },
      photos: { [key]: this.sortPhotosByDisplayOrder(record.photos) },
      sync: { pendingJobIds: [key], lastSyncedAt: null },
    };
  }

  private sortPhotosByDisplayOrder(photos: PropertyPhoto[]): PropertyPhoto[] {
    return photos
      .map((photo, index) => ({ photo, index }))
      .sort((left, right) => {
        const leftOrder = this.displayOrderNumber(left.photo.displayOrder);
        const rightOrder = this.displayOrderNumber(right.photo.displayOrder);
        if (leftOrder === null && rightOrder === null) return left.index - right.index;
        if (leftOrder === null) return 1;
        if (rightOrder === null) return -1;
        return leftOrder - rightOrder || left.index - right.index;
      })
      .map(({ photo }) => photo);
  }

  private displayOrderNumber(displayOrder?: string | null): number | null {
    if (!displayOrder) return null;
    const numberValue = Number(displayOrder);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
  }

  private syncPropertyInfo(jobId: number | string, propertyInfo: Partial<PropertyInfo> | null): Record<string, unknown> | null {
    if (!propertyInfo) return null;

    const target = { ...propertyInfo, jobId: Number(jobId) || jobId } as Record<string, unknown>;
    const buildings = this.parseComparableArray(target['buildings']).filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    const mainDwellingIndex = buildings.findIndex((building) => String(building['name'] || '').trim().toLowerCase() === 'main dwelling');
    if (mainDwellingIndex < 0) return target;

    const mainDwelling = buildings[mainDwellingIndex];
    target['buildings'] = JSON.stringify(buildings.filter((_, index) => index !== mainDwellingIndex));
    this.applyMainDwellingToPropertyInfo(target, mainDwelling);
    return target;
  }

  private applyMainDwellingToPropertyInfo(target: Record<string, unknown>, mainDwelling: Record<string, unknown>): void {
    const fieldMap: Record<string, string> = {
      valuationType: 'valuationType',
      valuationTypeId: 'valuationTypeId',
      valuationTypeDescription: 'valuationTypeDescription',
      levels: 'levels',
      mainWalls: 'mainWalls',
      style: 'style',
      windowFrames: 'windowFrames',
      streetAppeal: 'streetAppeal',
      roof: 'roof',
      foundations: 'foundations',
      interiorLinings: 'interiorLinings',
      flooring: 'flooring',
      ceilingLinings: 'ceilingLinings',
      frame: 'frame',
      bedrooms: 'bedrooms',
      bathrooms: 'bathrooms',
      numberOfLivingAreas: 'livingAreas',
      undercoverCarSpaces: 'undercoverCarSpaces',
      carAccommodation: 'mainCarAccomType',
      pcItems: 'accommodationPlus',
      features: 'fixturesAndFeatures',
      chattels: 'chattels',
      briefDescription: 'briefDescription',
      livingAreasSqm: 'livingAreasSqm',
      yearOfConstruction: 'yearOfConstruction',
      outdoorAreasSqm: 'outdoorAreasSqm',
      yearOfAdditions: 'yearOfAdditions',
      otherAreasSqm: 'otherAreasSqm',
      outdoorArea: 'outdoorArea',
      carAreasSqm: 'carAreasSqm',
      significantImprovements: 'significantImprovements',
      internalLayout: 'internalLayout',
      secondaryImprovements: 'secondaryImprovements',
      internalCondition: 'internalCondition',
      externalCondition: 'externalCondition',
      carSpaces: 'carSpaces',
    };

    Object.entries(fieldMap).forEach(([sourceKey, targetKey]) => {
      if (mainDwelling[sourceKey] !== undefined) target[targetKey] = mainDwelling[sourceKey];
    });

    if (mainDwelling['yearOfConstruction'] !== undefined) target['yearBuilt'] = mainDwelling['yearOfConstruction'];
    if (mainDwelling['floorAreas'] !== undefined) target['floorAreas'] = mainDwelling['floorAreas'];
  }

  private documentJobSyncPayload(jobId: number | string, record: JobSyncRecord, uploadedPhotos: ImageUploadTarget[], uploadablePhotos: PropertyPhoto[]): Record<string, unknown> {
    const key = String(jobId);
    const uploadedByClientId = new Map(uploadedPhotos.filter((photo) => !!photo.clientUploadId).map((photo) => [photo.clientUploadId!, photo]));
    const orderedRecordPhotos = this.sortPhotosByDisplayOrder(record.photos);
    const photos = orderedRecordPhotos.map((photo, index) => {
      const uploaded = this.uploadedPhotoTarget(photo, index, uploadablePhotos, uploadedByClientId);
      if (uploaded) return this.uploadedPhotoDocumentRecord(photo, uploaded);
      if (this.isUploadedPhoto(photo)) return this.uploadedPhotoDocumentRecord(photo);
      return photo;
    });
    const dwellingImages = orderedRecordPhotos
      .map((photo, index) => {
        const uploaded = this.uploadedPhotoTarget(photo, index, uploadablePhotos, uploadedByClientId);
        return {
          mcProId: photo.mcProId ?? uploaded?.mcProId ?? null,
          imageName: photo.label ?? uploaded?.label ?? null,
          description: photo.description ?? uploaded?.description ?? null,
          imageType: photo.imageType ?? null,
          displayOrder: photo.displayOrder ?? null,
          documentUid: uploaded?.documentUid ?? photo.documentUid ?? null,
          md5Hash: uploaded?.md5Hash ?? photo.md5Hash ?? null,
          s3Bucket: uploaded?.s3Bucket ?? photo.s3Bucket ?? null,
          s3Key: uploaded?.s3Key ?? photo.s3Key ?? null,
        };
      })
      .filter((photo) => !!photo.documentUid && !!photo.md5Hash);
    return {
      jobs: [{ ...record.job, id: Number(jobId) || jobId }],
      propertyInfo: { [key]: this.syncPropertyInfo(jobId, record.propertyInfo) },
      photos: { [key]: photos },
      dwellingImages,
      sync: { pendingJobIds: [key], lastSyncedAt: null },
    };
  }

  private photosOnlyJobSyncPayload(jobId: number | string, record: JobSyncRecord, serverRecord: JobSyncRecord, uploadedPhotos: ImageUploadTarget[], uploadablePhotos: PropertyPhoto[]): Record<string, unknown> {
    const key = String(jobId);
    const documentPayload = this.documentJobSyncPayload(jobId, record, uploadedPhotos, uploadablePhotos);
    const notes = this.photoNotesPropertyInfo(jobId, record.propertyInfo);
    return {
      jobs: [{ ...serverRecord.job, id: Number(jobId) || jobId }],
      photos: documentPayload['photos'],
      dwellingImages: documentPayload['dwellingImages'],
      propertyInfo: { [key]: notes },
      sync: { pendingJobIds: [key], lastSyncedAt: null },
    };
  }

  private photoNotesPropertyInfo(jobId: number | string, propertyInfo: Partial<PropertyInfo> | null): Record<string, unknown> | null {
    const notes: Record<string, unknown> = {};
    notes['jobId'] = Number(jobId) || jobId;
    if (propertyInfo && propertyInfo.notes !== undefined) notes['notes'] = propertyInfo.notes;
    if (propertyInfo && propertyInfo.drawingData !== undefined) notes['drawingData'] = propertyInfo.drawingData;
    return notes;
  }

  private localJobSyncPayloadWithUploadedPhotos(jobId: number | string, record: JobSyncRecord, uploadedPhotos: ImageUploadTarget[], uploadablePhotos: PropertyPhoto[]): Record<string, unknown> {
    const uploadedByClientId = new Map(uploadedPhotos.filter((photo) => !!photo.clientUploadId).map((photo) => [photo.clientUploadId!, photo]));
    const photos = record.photos.map((photo, index) => {
      const uploaded = this.uploadedPhotoTarget(photo, index, uploadablePhotos, uploadedByClientId);
      return uploaded ? { ...photo, ...this.uploadedPhotoMetadata(photo, uploaded) } : photo;
    });
    return this.jobSyncPayload(jobId, { ...record, photos });
  }

  private uploadedPhotoDocumentRecord(photo: PropertyPhoto, uploaded?: ImageUploadTarget): Record<string, unknown> {
    return {
      id: photo.id,
      jobId: photo.jobId,
      label: photo.label,
      description: photo.description ?? null,
      imageType: photo.imageType ?? null,
      displayOrder: photo.displayOrder ?? null,
      ...this.uploadedPhotoMetadata(photo, uploaded),
    };
  }

  private uploadedPhotoMetadata(photo: PropertyPhoto, uploaded?: ImageUploadTarget): Pick<PropertyPhoto, 'mcProId' | 'documentUid' | 'md5Hash' | 's3Bucket' | 's3Key' | 'contentType' | 'imageType'> {
    return {
      mcProId: photo.mcProId ?? uploaded?.mcProId ?? null,
      documentUid: uploaded?.documentUid ?? photo.documentUid ?? null,
      md5Hash: uploaded?.md5Hash ?? photo.md5Hash ?? null,
      s3Bucket: uploaded?.s3Bucket ?? photo.s3Bucket ?? null,
      s3Key: uploaded?.s3Key ?? photo.s3Key ?? null,
      contentType: uploaded?.contentType ?? photo.contentType ?? null,
      imageType: photo.imageType ?? null,
    };
  }

  private presignJobImages(jobId: number | string, photos: ImagePresignPhotoRequest[]): Observable<ImagePresignResponse> {
    return this.http.post<ImagePresignResponse>(`${REMOTE_API_BASE}/${encodeURIComponent(String(jobId))}/photos/presign`, { photos });
  }

  private imagePresignPhotoRequests(photos: PropertyPhoto[]): ImagePresignPhotoRequest[] {
    return photos.map((photo, index) => {
      const image = this.dataUrlBytes(photo.photoUrl, 'application/octet-stream');
      const arrayBuffer = this.arrayBufferFromBytes(image.bytes);
      return {
        clientUploadId: this.clientUploadId(photo, index),
        id: photo.id,
        jobId: photo.jobId,
        mcProId: photo.mcProId ?? null,
        label: photo.label,
        description: photo.description ?? null,
        contentType: image.contentType,
        md5Hash: photoCompatibilityHash(arrayBuffer),
      };
    });
  }

  private clientUploadId(photo: PropertyPhoto, index: number): string {
    return `${photo.jobId || 'job'}:${photo.id || 'photo'}:${index}`;
  }

  private attachClientUploadIds(targets: ImageUploadTarget[], requests: ImagePresignPhotoRequest[]): ImageUploadTarget[] {
    return targets.map((target, index) => ({
      ...target,
      clientUploadId: target.clientUploadId || requests[index]?.clientUploadId || `${target.jobId || 'job'}:${target.id || 'photo'}:${index}`,
    }));
  }

  private uploadedPhotoTarget(
    photo: PropertyPhoto,
    recordIndex: number,
    uploadablePhotos: PropertyPhoto[],
    uploadedByClientId: Map<string, ImageUploadTarget>,
  ): ImageUploadTarget | undefined {
    const directMatch = uploadedByClientId.get(this.clientUploadId(photo, recordIndex));
    if (directMatch) return directMatch;
    const uploadableIndex = uploadablePhotos.indexOf(photo);
    if (uploadableIndex >= 0) return uploadedByClientId.get(this.clientUploadId(photo, uploadableIndex));
    const matchedIndex = uploadablePhotos.findIndex((uploadablePhoto) => String(uploadablePhoto.id) === String(photo.id) && String(uploadablePhoto.jobId) === String(photo.jobId));
    return matchedIndex >= 0 ? uploadedByClientId.get(this.clientUploadId(photo, matchedIndex)) : undefined;
  }

  private remoteJobIdentifier(draft: JobSyncDraft, record: JobSyncRecord): number | string {
    const job = record.job as Partial<Job>;
    return job.mcProId ?? draft.jobNumber ?? draft.jobId;
  }

  private async uploadImagesToS3(photos: PropertyPhoto[], targets: ImageUploadTarget[]): Promise<void> {
    const photosByClientId = new Map(photos.map((photo, index) => [this.clientUploadId(photo, index), photo]));
    await Promise.all(targets.map(async (target, index) => {
      const clientUploadId = target.clientUploadId || `${target.jobId || 'job'}:${target.id || 'photo'}:${index}`;
      const photo = photosByClientId.get(clientUploadId) || photos[index];
      if (!photo) throw new Error(`Missing local image data for photo ${clientUploadId}.`);
      const blob = this.dataUrlBlob(photo.photoUrl, target.contentType);
      const response = await fetch(target.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': target.contentType,
          'x-amz-meta-documentuid': target.documentUid,
          'x-amz-meta-md5hash': target.md5Hash,
          'x-amz-meta-source': 'tops-offline-ui',
        },
        body: blob,
      });
      if (!response.ok) throw new Error(`Failed to upload photo ${target.id} to S3 (${response.status}).`);
    }));
  }

  private dataUrlBlob(value: string, fallbackContentType: string): Blob {
    const image = this.dataUrlBytes(value, fallbackContentType);
    return new Blob([this.arrayBufferFromBytes(image.bytes)], { type: image.contentType });
  }

  private arrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }

  private dataUrlBytes(value: string, fallbackContentType: string): { contentType: string; bytes: Uint8Array } {
    const match = value.match(/^data:([^;,]+)?(;base64)?,(.*)$/s);
    if (!match) throw new Error('Photo data is not a supported data URL.');
    const contentType = match[1] || fallbackContentType || 'application/octet-stream';
    const raw = match[2] ? atob(match[3] || '') : decodeURIComponent(match[3] || '');
    const bytes = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
    return { contentType, bytes };
  }

  private isDataImageUrl(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.startsWith('data:image/');
  }

  private isUploadedPhoto(photo: PropertyPhoto): boolean {
    return !!photo.documentUid || !!photo.s3Key;
  }

  private isPendingLocalUploadPhoto(photo: PropertyPhoto): boolean {
    return this.isDataImageUrl(photo.photoUrl) && !this.isUploadedPhoto(photo);
  }

  private fieldValuesOnlyJobSyncPayload(jobId: number | string, record: JobSyncRecord): Record<string, unknown> {
    const key = String(jobId);
    const propertyInfo = this.syncPropertyInfo(jobId, record.propertyInfo);
    return {
      jobs: [{ ...record.job, id: Number(jobId) || jobId }],
      propertyInfo: { [key]: propertyInfo },
      sync: { pendingJobIds: [key], lastSyncedAt: null },
    };
  }

  private createLocalUploadedPhoto(jobId: number | string, formData: FormData): Observable<PropertyPhoto> {
    const file = formData.get('photo');
    const label = String(formData.get('label') || 'Property photo');
    if (!(file instanceof File)) {
      return throwError(() => new Error('No photo was selected.'));
    }

    return new Observable<PropertyPhoto>((subscriber) => {
      const reader = new FileReader();
      reader.onload = () => {
        const photo = this.createLocalPhoto(jobId, String(reader.result || ''), label);
        subscriber.next(photo);
        subscriber.complete();
      };
      reader.onerror = () => subscriber.error(new Error('Failed to read the selected photo.'));
      reader.readAsDataURL(file);
    });
  }

  private createApiUploadedPhoto(jobId: number | string, formData: FormData): Observable<PropertyPhoto> {
    const file = formData.get('photo');
    const label = String(formData.get('label') || 'Property photo');
    if (!(file instanceof File)) {
      return throwError(() => new Error('No photo was selected.'));
    }

    return new Observable<PropertyPhoto>((subscriber) => {
      const reader = new FileReader();
      reader.onload = () => {
        this.saveWebcamPhoto(jobId, String(reader.result || ''), label).subscribe(subscriber);
      };
      reader.onerror = () => subscriber.error(new Error('Failed to read the selected photo.'));
      reader.readAsDataURL(file);
    }).pipe(
      catchError((error) => {
        if (!this.isBackendUnavailable(error)) return throwError(() => error);
        return this.createLocalUploadedPhoto(jobId, formData);
      }),
    );
  }

  private createLocalPhoto(jobId: number | string, photoUrl: string, label: string | null, description: string | null = null, imageType: string | null = null, displayOrder: string | null = null): PropertyPhoto {
    const photos = this.readLocalPhotos(jobId);
    const md5Hash = this.photoMd5Hash(photoUrl);
    const existingPhoto = photos.find((photo) => this.samePhotoHash(photo.md5Hash, md5Hash));
    if (existingPhoto) return existingPhoto;

    const photo: PropertyPhoto = {
      id: this.nextLocalPhotoId(photos),
      jobId: Number(jobId) || 0,
      photoUrl,
      label,
      description,
      imageType,
      mcProId: this.readLocalJobs().find((job) => String(job.id) === String(jobId))?.mcProId ?? null,
      md5Hash,
      displayOrder: this.normalizeDisplayOrder(displayOrder),
    };
    this.writeLocalPhotos(jobId, [photo, ...photos]);
    return photo;
  }

  private writeImportedLocalPhotos(jobId: number | string, importedPhotos: PropertyPhoto[]): PropertyPhoto[] {
    const existingPhotos = this.readLocalPhotos(jobId).filter((photo) => !!photo.photoUrl);
    const imported = importedPhotos.map((photo, index) => ({
      ...photo,
      id: Number(photo.id) || Date.now() + index,
      jobId: Number(jobId) || 0,
      mcProId: photo.mcProId ?? this.readLocalJobs().find((job) => String(job.id) === String(jobId))?.mcProId ?? null,
    }));
    this.writeLocalPhotos(jobId, this.mergePhotosByUrl(imported, existingPhotos));
    return imported;
  }

  private mergePhotosByUrl(primaryPhotos: PropertyPhoto[], secondaryPhotos: PropertyPhoto[]): PropertyPhoto[] {
    const seen = new Set<string>();
    return [...primaryPhotos, ...secondaryPhotos].filter((photo) => {
      const key = this.photoIdentityKey(photo);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private photoIdentityKey(photo: PropertyPhoto): string {
    return String(photo.md5Hash || photo.documentUid || photo.s3Key || photo.photoUrl || '').trim();
  }

  private photoMd5Hash(photoUrl: string): string | null {
    if (!this.isDataImageUrl(photoUrl)) return null;
    const image = this.dataUrlBytes(photoUrl, 'application/octet-stream');
    return photoCompatibilityHash(this.arrayBufferFromBytes(image.bytes));
  }

  private samePhotoHash(left: string | null | undefined, right: string | null | undefined): boolean {
    if (!left || !right || left.length !== right.length) return false;
    let difference = 0;
    for (let index = 0; index < left.length; index += 1) {
      difference |= left.charCodeAt(index) ^ right.charCodeAt(index);
    }
    return difference === 0;
  }

  private async materializeImportedPhotos(photos: PropertyPhoto[]): Promise<PropertyPhoto[]> {
    return Promise.all(photos.map((photo) => this.materializeImportedPhoto(photo)));
  }

  private async materializeImportedPhoto(photo: PropertyPhoto): Promise<PropertyPhoto> {
    if (this.isDataImageUrl(photo.photoUrl)) {
      return { ...photo, md5Hash: photo.md5Hash ?? this.photoMd5Hash(photo.photoUrl) };
    }
    if (!/^https?:\/\//i.test(photo.photoUrl)) return photo;

    try {
      const response = await fetch(photo.photoUrl);
      if (!response.ok) return photo;
      const sourceBlob = await response.blob();
      const blob = sourceBlob.type
        ? sourceBlob
        : new Blob([sourceBlob], { type: photo.contentType || 'application/octet-stream' });
      const dataUrl = await this.blobToDataUrl(blob);
      return {
        ...photo,
        photoUrl: dataUrl,
        md5Hash: photo.md5Hash ?? this.photoMd5Hash(dataUrl),
        contentType: photo.contentType ?? (blob.type || null),
      };
    } catch {
      return photo;
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read imported photo.'));
      reader.readAsDataURL(blob);
    });
  }

  private normalizePhotoResponse(value: unknown, fallbackJobId: number | string): PropertyPhoto {
    const photo = value && typeof value === 'object' ? value as Record<string, unknown> : {};
    const rawImage = this.remotePhotoImageSource(photo);
    return {
      id: Number(photo['id'] ?? photo['photoId'] ?? Date.now()),
      jobId: Number(photo['jobId'] ?? fallbackJobId) || 0,
      photoUrl: this.normalizeImageSource(rawImage),
      label: this.remoteNullableString(photo, 'label', 'imageName', 'Description'),
      description: this.remoteNullableString(photo, 'description', 'Description'),
      imageType: this.remoteNullableString(photo, 'imageType', 'ImageType'),
      mcProId: photo['mcProId'] as string | number | null | undefined ?? null,
      documentUid: this.remoteNullableString(photo, 'documentUid', 'documentUID', 'DocumentUID'),
      md5Hash: this.remoteNullableString(photo, 'md5Hash', 'MD5Hash'),
      s3Bucket: this.remoteNullableString(photo, 's3Bucket', 'S3Bucket'),
      s3Key: this.remoteNullableString(photo, 's3Key', 'S3Key'),
      contentType: this.remoteNullableString(photo, 'contentType'),
      fileExtension: this.remoteNullableString(photo, 'fileExtension', 'FileExtension'),
      displayOrder: this.normalizeDisplayOrder(photo['displayOrder'] ?? photo['DisplayOrder']),
    };
  }

  private normalizeDisplayOrder(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    if (!Number.isInteger(numberValue) || numberValue < 0 || numberValue > 100) return null;
    return numberValue.toString().padStart(2, '0');
  }

  private normalizePhotoList(value: unknown, fallbackJobId: number | string): PropertyPhoto[] {
    return this.photoArray(value)
      .map((photo) => this.normalizePhotoResponse(photo, fallbackJobId))
      .filter((photo) => !!photo.photoUrl);
  }

  private photoArray(value: unknown): unknown[] {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object') return [];
    const response = value as Record<string, unknown>;
    const nested = response['photos'] ?? response['data'] ?? response['items'] ?? response['dwellingImages'];
    return Array.isArray(nested) ? nested : [];
  }

  private normalizeImageSource(value: unknown): string {
    if (typeof value !== 'string') return '';
    const image = value.trim();
    if (!image) return '';
    if (/^(https?:|data:image\/|blob:)/i.test(image)) return image;
    if (/^0x[0-9a-f]+$/i.test(image) || /^[0-9a-f]+$/i.test(image)) {
      const hex = image.startsWith('0x') || image.startsWith('0X') ? image.slice(2) : image;
      const mimeType = this.hexImageMimeType(hex);
      let binary = '';
      for (let index = 0; index < hex.length; index += 2) {
        binary += String.fromCharCode(parseInt(hex.slice(index, index + 2), 16));
      }
      return `data:${mimeType};base64,${btoa(binary)}`;
    }
    return `data:${this.base64ImageMimeType(image)};base64,${image}`;
  }

  private hexImageMimeType(hex: string): string {
    const signature = hex.slice(0, 16).toUpperCase();
    if (signature.startsWith('FFD8FF')) return 'image/jpeg';
    if (signature.startsWith('89504E47')) return 'image/png';
    if (signature.startsWith('47494638')) return 'image/gif';
    if (signature.startsWith('52494646')) return 'image/webp';
    return 'image/jpeg';
  }

  private base64ImageMimeType(base64: string): string {
    if (base64.startsWith('/9j/')) return 'image/jpeg';
    if (base64.startsWith('iVBOR')) return 'image/png';
    if (base64.startsWith('R0lGOD')) return 'image/gif';
    if (base64.startsWith('UklGR')) return 'image/webp';
    return 'image/jpeg';
  }

  private updateLocalPhoto(jobId: number | string, photoId: number | string, payload: PhotoDetailsPayload, originalError: unknown): PropertyPhoto {
    const photos = this.readLocalPhotos(jobId);
    const index = photos.findIndex((photo) => String(photo.id) === String(photoId));
    if (index >= 0) {
      const updated = {
        ...photos[index],
        label: payload.label?.trim() || null,
        description: payload.description?.trim() || null,
        imageType: payload.imageType?.trim() || null,
        displayOrder: this.normalizeDisplayOrder(payload.displayOrder),
      };
      photos[index] = updated;
      this.writeLocalPhotos(jobId, photos);
      return updated;
    }

    throw originalError;
  }

  private deleteLocalPhoto(photoId: number | string): void {
    this.storageKeys()
      .filter((key) => key.startsWith(this.localPhotosPrefix))
      .forEach((key) => {
        const jobId = key.slice(this.localPhotosPrefix.length);
        this.writeLocalPhotos(jobId, this.readLocalPhotos(jobId).filter((photo) => String(photo.id) !== String(photoId)));
      });
  }

  private writeLocalPhotos(jobId: number | string, photos: PropertyPhoto[]): void {
    this.writeStorage(this.photosKey(jobId), JSON.stringify(photos));
  }

  private nextLocalJobId(jobs: Job[]): number {
    return jobs.reduce((maxId, job) => Math.max(maxId, Number(job.id) || 0), 0) + 1;
  }

  private nextLocalPhotoId(photos: PropertyPhoto[]): number {
    const nextExistingId = photos.reduce((maxId, photo) => Math.max(maxId, Number(photo.id) || 0), 0) + 1;
    this.localPhotoIdSequence = Math.max(this.localPhotoIdSequence + 1, nextExistingId);
    return this.localPhotoIdSequence;
  }

  private propertyInfoKey(jobId: number | string): string {
    return `${this.localPropertyInfoPrefix}${jobId}`;
  }

  private photosKey(jobId: number | string): string {
    return `${this.localPhotosPrefix}${jobId}`;
  }

  private storageKeys(): string[] {
    return this.storage.keys();
  }

  private readStorage(key = this.localJobsKey): string | null {
    return this.storage.getItem(key);
  }

  private writeStorage(key: string, value: string): void {
    this.storage.setItem(key, value);
  }

  private removeStorage(key: string): void {
    this.storage.removeItem(key);
  }

  private writeLocalJobs(jobs: Job[]): void {
    this.memoryJobs = [...jobs];
    this.writeStorage(this.localJobsKey, JSON.stringify(jobs));
  }
}
